import {
  CARD_DESCRIPTION_FIELD_HINT,
  CARD_DESCRIPTION_LIMIT_MESSAGE,
  CARD_DESCRIPTION_MAX,
  clampDescription,
  clampDescriptionField,
  summarizeReportBody
} from "./chunk-6RHVH33O.js";

// src/types/common.ts
function isCuid(str) {
  return /^c[a-z0-9]{24}$/.test(str);
}

// src/types/message-types.ts
var MESSAGE_KINDS = [
  "chat",
  "human_input",
  "agent_milestone",
  "lifecycle",
  "external",
  "detail"
];
var AGENT_MILESTONE_SLUGS = [
  "plan_ready",
  "implementation_complete",
  "blocked",
  "review_approved",
  "review_changes_requested"
];
var EXTERNAL_AGENT_MESSAGE_SOURCE = "external_agent";
function deriveMessageSource(metadata) {
  if (!metadata) return void 0;
  switch (metadata.type) {
    case "system_notification":
    case "automated_feedback":
      return metadata.source;
    case "code_review_result":
      return metadata.source === "changes_requested" ? "review_trigger" : "code_review_approved";
    case "agent_mention":
      return EXTERNAL_AGENT_MESSAGE_SOURCE;
    default:
      return void 0;
  }
}

// src/constants/models.ts
var DEFAULT_SONNET_MODEL = "claude-sonnet-5";
var DEFAULT_OPUS_MODEL = "claude-opus-5";
var DEFAULT_HAIKU_MODEL = "claude-haiku-4-5-20251001";
var FABLE_MODEL = "claude-fable-5";
var PREVIOUS_SONNET_MODEL = "claude-sonnet-4-6";
var PREVIOUS_OPUS_MODEL = "claude-opus-4-8";

// src/types/pty-stream.ts
var PTY_STREAM_PORT_BASE = 7420;
var PTY_STREAM_PORT_ATTEMPTS = 8;
function isPtyStreamPort(port) {
  return Number.isInteger(port) && port >= PTY_STREAM_PORT_BASE && port < PTY_STREAM_PORT_BASE + PTY_STREAM_PORT_ATTEMPTS;
}
function encodePtyStreamFrame(frame) {
  return `${JSON.stringify(frame)}
`;
}
function isRecord(value) {
  return typeof value === "object" && value !== null;
}
function parsePtyStreamFrame(line) {
  if (!line) return null;
  let parsed;
  try {
    parsed = JSON.parse(line);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || typeof parsed.t !== "string") return null;
  switch (parsed.t) {
    case "hello":
    case "data":
    case "ended":
    case "input":
    case "resize":
      return parsed;
    default:
      return null;
  }
}
var PtyStreamFrameReader = class {
  constructor(onFrame) {
    this.onFrame = onFrame;
  }
  onFrame;
  buffer = "";
  push(chunk) {
    this.buffer += chunk;
    let index = this.buffer.indexOf("\n");
    while (index >= 0) {
      const line = this.buffer.slice(0, index);
      this.buffer = this.buffer.slice(index + 1);
      const frame = parsePtyStreamFrame(line);
      if (frame) this.onFrame(frame);
      index = this.buffer.indexOf("\n");
    }
  }
};

// src/types/project-settings-types.ts
import { z } from "zod";
var PREVIEW_PORT_DENY_LIST = [
  5432,
  6379,
  9200,
  ...Array.from({ length: PTY_STREAM_PORT_ATTEMPTS }, (_, i) => PTY_STREAM_PORT_BASE + i)
];
function isAllowablePreviewPort(port) {
  if (typeof port !== "number" || !Number.isInteger(port)) return false;
  if (port < 1 || port > 65535) return false;
  return !PREVIEW_PORT_DENY_LIST.includes(port);
}
function sanitizeSessionPreviewPorts(ports) {
  if (!Array.isArray(ports)) return [];
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const raw of ports) {
    if (!raw || typeof raw !== "object") continue;
    const portValue = raw.port;
    if (!isAllowablePreviewPort(portValue)) continue;
    if (seen.has(portValue)) continue;
    seen.add(portValue);
    const labelValue = raw.label;
    const visValue = raw.visibility;
    out.push({
      port: portValue,
      ...typeof labelValue === "string" ? { label: labelValue } : {},
      ...visValue === "public" || visValue === "private" ? { visibility: visValue } : {}
    });
  }
  return out;
}
function resolveAllowedPreviewPorts(sources) {
  const set = /* @__PURE__ */ new Set();
  if (Array.isArray(sources.sessionPreviewPorts)) {
    for (const entry of sources.sessionPreviewPorts) {
      if (isAllowablePreviewPort(entry?.port)) set.add(entry.port);
    }
  }
  return [...set].sort((a, b) => a - b);
}
function pickPreferredPreviewPort(allowed, preferred) {
  if (preferred !== null && preferred !== void 0 && allowed.includes(preferred))
    return preferred;
  return allowed[0] ?? null;
}
function normalizeCheckpointPath(value) {
  let normalized = value.trim().replace(/\/{2,}/g, "/");
  normalized = normalized.split("/").filter((segment) => segment !== ".").join("/");
  while (normalized.endsWith("/")) normalized = normalized.slice(0, -1);
  return normalized;
}
var checkpointPathSchema = z.string().transform(normalizeCheckpointPath).pipe(
  z.string().min(1).refine((value) => value !== ".", "Checkpoint paths must name a repository entry").refine((value) => !value.startsWith("/"), "Checkpoint paths must be repository-relative").refine(
    (value) => !/^[A-Za-z]:[\\/]/.test(value) && !value.includes("\\"),
    "Checkpoint paths must use repository-relative POSIX syntax"
  ).refine(
    (value) => !value.split("/").includes(".."),
    "Checkpoint paths must not traverse a parent directory"
  )
);
var secretNameSchema = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/);
var checkpointKeySchema = z.string().regex(/^[0-9a-f]{64}$/);
var checkpointDigestRefSchema = z.string().regex(/^[^\s@]+@sha256:[0-9a-f]{64}$/);
var ACTIONS_PREBAKE_REGISTRY_PATTERN = /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?(?::(?:[1-9][0-9]{0,4}))?(?:\/[a-z0-9]+(?:[._-][a-z0-9]+)*)*$/;
var actionsPrebakeRegistrySchema = z.string().trim().min(1).regex(
  ACTIONS_PREBAKE_REGISTRY_PATTERN,
  "Actions prebake registry must be a lowercase host[:port] with an optional path prefix"
).refine((value) => {
  const port = /:([0-9]+)(?:\/|$)/.exec(value)?.[1];
  return !port || Number(port) <= 65535;
}, "Actions prebake registry port must be between 1 and 65535");
function uniqueSortedArray(item, minimum = 0) {
  return z.array(item).min(minimum).superRefine((values, ctx) => {
    if (new Set(values).size !== values.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Duplicate values are not allowed" });
    }
  }).transform((values) => [...values].sort());
}
var projectCheckpointSettingsSchema = z.object({
  enabled: z.literal(true),
  cacheCommand: z.string().trim().min(1),
  cacheInputPaths: uniqueSortedArray(checkpointPathSchema, 1),
  reusableArtifactPaths: uniqueSortedArray(checkpointPathSchema, 1),
  finalizeCommand: z.string().trim().min(1),
  credentialEpoch: z.string().trim().min(1),
  requiredSecretNames: uniqueSortedArray(secretNameSchema).optional(),
  optionalSecretNames: uniqueSortedArray(secretNameSchema).optional()
}).superRefine((checkpoint, ctx) => {
  const required = new Set(checkpoint.requiredSecretNames ?? []);
  for (const name of checkpoint.optionalSecretNames ?? []) {
    if (required.has(name)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["optionalSecretNames"],
        message: "A secret cannot be both required and optional"
      });
    }
  }
});
function resolveProjectCheckpointSettings(settings) {
  if (settings?.checkpoint?.enabled !== true) return null;
  const parsed = projectCheckpointSettingsSchema.safeParse(settings.checkpoint);
  return parsed.success ? parsed.data : null;
}
function isPlainProjectSettings(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function coerceProjectSettings(value) {
  return isPlainProjectSettings(value) ? value : {};
}
function resolveMaxOpenPrs(settings) {
  const configured = settings?.maxOpenPrs;
  return typeof configured === "number" && Number.isInteger(configured) && configured >= 0 ? configured : null;
}
var PROJECT_FUNCTION_IDS = [
  "identification",
  "reportsHandler",
  "prReview",
  "reporterEmail",
  "pmChat",
  "releaseNotes"
];
var PROJECT_FUNCTION_DEFAULTS = {
  identification: {
    model: DEFAULT_SONNET_MODEL,
    effort: "low",
    thinking: { type: "disabled" }
  },
  reportsHandler: {
    model: DEFAULT_SONNET_MODEL,
    effort: "high",
    thinking: { type: "disabled" }
  },
  prReview: {
    model: DEFAULT_SONNET_MODEL,
    effort: "low",
    thinking: { type: "disabled" }
  },
  reporterEmail: {
    model: DEFAULT_SONNET_MODEL,
    effort: "high",
    thinking: { type: "disabled" }
  },
  pmChat: {
    model: DEFAULT_SONNET_MODEL,
    effort: "medium",
    thinking: { type: "disabled" }
  },
  releaseNotes: {
    model: DEFAULT_SONNET_MODEL,
    effort: "medium",
    thinking: { type: "disabled" }
  }
};
function resolveProjectFunctionConfig(settings, functionId) {
  const defaults = PROJECT_FUNCTION_DEFAULTS[functionId];
  const override = settings?.functionSettings?.[functionId];
  return {
    model: override?.model ?? defaults.model,
    effort: override?.effort ?? defaults.effort,
    thinking: override?.thinking ?? defaults.thinking,
    instructions: override?.instructions
  };
}
var MAX_AGENT_FLAVOR_LENGTH = 2e3;
function applyAgentFlavor(instructions, settings) {
  const flavor = settings?.agentFlavor?.trim();
  if (!flavor) return instructions;
  const section = `Flavor:
${flavor}`;
  return instructions?.trim() ? `${instructions}

${section}` : section;
}
function resolveCodeReviewMode(settings) {
  return settings?.codeReviewMode ?? "off";
}
function resolveVerifiedLinksPrCheck(settings) {
  return settings?.verifiedLinksPrCheck === true;
}
var TRIAGE_INCLUDE_VALUES = ["all", "incidents", "suggestions"];
function resolveTriageSettings(settings) {
  const triage = settings?.triage;
  if (!triage || typeof triage !== "object") return null;
  const { agentId, userId, include } = triage;
  if (typeof agentId !== "string" || agentId.length === 0) return null;
  if (typeof userId !== "string" || userId.length === 0) return null;
  if (!TRIAGE_INCLUDE_VALUES.includes(include)) return null;
  return { agentId, userId, include };
}
function resolveGcpEnvResources(settings) {
  return settings?.gcpEnvResources ?? {};
}

// src/types/ops-types.ts
var OPS_ENVIRONMENTS = ["live", "dev"];
function resolveOpsSettings(settings) {
  return settings?.ops ?? {};
}
function resolveOpsEnv(settings, env) {
  return resolveOpsSettings(settings).environments?.[env] ?? {};
}
function buildOpsEnvConfigDTO(conn) {
  return {
    redisUrlSet: Boolean(conn.redisUrlEnc),
    queueDiscovery: Boolean(conn.queueDiscovery),
    queueNames: conn.queueNames ?? [],
    elasticsearchUrlSet: Boolean(conn.elasticsearchUrlEnc)
  };
}
function buildOpsConfigDTO(settings) {
  const ops = resolveOpsSettings(settings);
  const envs = ops.environments ?? {};
  return {
    environments: {
      live: buildOpsEnvConfigDTO(envs.live ?? {}),
      dev: buildOpsEnvConfigDTO(envs.dev ?? {})
    },
    readOnly: Boolean(ops.readOnly)
  };
}
function mergeOpsEnvSettings(settings, env, patch) {
  const ops = { ...settings.ops ?? {} };
  const environments = {
    ...ops.environments ?? {}
  };
  if (patch === null) {
    delete environments[env];
  } else {
    const next = { ...environments[env] ?? {} };
    for (const [key, value] of Object.entries(patch)) {
      if (value !== void 0) next[key] = value;
    }
    environments[env] = next;
  }
  ops.environments = environments;
  return { ...settings, ops };
}
function clearOpsEnvFields(settings, env, fields) {
  const ops = { ...settings.ops ?? {} };
  const environments = {
    ...ops.environments ?? {}
  };
  const next = { ...environments[env] ?? {} };
  for (const key of fields) delete next[key];
  if (Object.keys(next).length === 0) {
    delete environments[env];
  } else {
    environments[env] = next;
  }
  ops.environments = environments;
  return { ...settings, ops };
}
function setOpsReadOnly(settings, readOnly) {
  const ops = { ...settings.ops ?? {}, readOnly };
  return { ...settings, ops };
}
function validateRedisUrl(value) {
  let parsed;
  try {
    parsed = new URL(value);
  } catch {
    return { ok: false, message: "Redis URL is not a valid URL" };
  }
  if (parsed.protocol !== "redis:" && parsed.protocol !== "rediss:") {
    return { ok: false, message: "Redis URL must use redis:// or rediss://" };
  }
  if (!parsed.hostname) {
    return { ok: false, message: "Redis URL is missing a host" };
  }
  return { ok: true };
}
function validateElasticsearchUrl(value) {
  let parsed;
  try {
    parsed = new URL(value);
  } catch {
    return { ok: false, message: "Elasticsearch URL is not a valid URL" };
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return { ok: false, message: "Elasticsearch URL must use http:// or https://" };
  }
  if (!parsed.hostname) {
    return { ok: false, message: "Elasticsearch URL is missing a host" };
  }
  return { ok: true };
}
var OPS_TAB_REGISTRY = [
  {
    id: "redis",
    labelKey: "opsTabRedis",
    requires: (conn) => Boolean(conn.redisUrlEnc)
  },
  {
    id: "queues",
    labelKey: "opsTabQueues",
    // Queues live inside Redis and only make sense once discovery is on.
    requires: (conn) => Boolean(conn.redisUrlEnc) && Boolean(conn.queueDiscovery)
  },
  {
    id: "elasticsearch",
    labelKey: "opsTabElasticsearch",
    requires: (conn) => Boolean(conn.elasticsearchUrlEnc)
  }
];
function resolveAvailableOpsTabsForEnv(conn) {
  return OPS_TAB_REGISTRY.filter((tab) => tab.requires(conn)).map((tab) => tab.id);
}
function resolveAvailableOpsTabs(settings) {
  const envs = resolveOpsSettings(settings).environments ?? {};
  const available = /* @__PURE__ */ new Set();
  for (const env of OPS_ENVIRONMENTS) {
    for (const id of resolveAvailableOpsTabsForEnv(envs[env] ?? {})) available.add(id);
  }
  return OPS_TAB_REGISTRY.filter((tab) => available.has(tab.id)).map((tab) => tab.id);
}
function isOpsEnvConfigured(conn) {
  return resolveAvailableOpsTabsForEnv(conn).length > 0;
}
function isOpsConfigured(settings) {
  return resolveAvailableOpsTabs(settings).length > 0;
}
function connFromEnvDTO(dto) {
  return {
    redisUrlEnc: dto.redisUrlSet ? "set" : void 0,
    queueDiscovery: dto.queueDiscovery,
    queueNames: dto.queueNames,
    elasticsearchUrlEnc: dto.elasticsearchUrlSet ? "set" : void 0
  };
}
function availableTabsForEnvDTO(dto) {
  return resolveAvailableOpsTabsForEnv(connFromEnvDTO(dto));
}
function isOpsEnvConfiguredDTO(dto) {
  return availableTabsForEnvDTO(dto).length > 0;
}
function availableTabsFromConfigDTO(config) {
  const available = /* @__PURE__ */ new Set();
  for (const env of OPS_ENVIRONMENTS) {
    for (const id of availableTabsForEnvDTO(config.environments[env])) available.add(id);
  }
  return OPS_TAB_REGISTRY.filter((tab) => available.has(tab.id)).map((tab) => tab.id);
}
var QUEUE_JOB_STATES = [
  "waiting",
  "active",
  "delayed",
  "completed",
  "failed",
  "paused"
];
var QUEUE_JOBS_PAGE_SIZE = 25;

// src/types/gcp-types.ts
var GCP_ENVS = ["prod", "dev", "claudespace"];

// src/types/grafana-types.ts
var GRAFANA_ENVS = ["prod", "dev"];
var GRAFANA_LOG_LEVELS = ["debug", "info", "warn", "error", "fatal"];
function resolveGrafanaSettings(settings) {
  return settings?.grafana ?? {};
}

// src/types/coding-agent.ts
var TUI_KINDS = ["claude-code", "opencode"];
var AGENT_PROVIDERS = ["anthropic", "openai", "zen"];
var AGENT_KEY_KINDS = ["oauth_token", "api_key", "chatgpt_oauth"];

// src/types/agent-project-types.ts
var TASK_STATUS_ORDER = [
  "Planning",
  "Open",
  "InProgress",
  "ReviewPR",
  "ReviewDev",
  "ReviewLive",
  "Complete",
  "Cancelled"
];

// src/types/risk-catalog.ts
import { z as z2 } from "zod";
var RISK_LEVELS = ["critical", "high", "medium", "low"];
var riskLevelSchema = z2.enum(RISK_LEVELS);
var RISK_RANK = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1
};
var DEFAULT_RISK_LEVELS = [
  {
    level: "critical",
    value: 4,
    label: "Critical",
    description: "Touches critical surface area; give it the closest review.",
    color: "#dc2626",
    ordinal: 0
  },
  {
    level: "high",
    value: 3,
    label: "Elevated",
    description: "Touches important surface area; review carefully.",
    color: "#ea580c",
    ordinal: 1
  },
  {
    level: "medium",
    value: 2,
    label: "Moderate",
    description: "Moderate surface area; normal review.",
    color: "#d97706",
    ordinal: 2
  },
  {
    level: "low",
    value: 1,
    label: "Minimal",
    description: "Small or isolated surface area.",
    color: "#64748b",
    ordinal: 3
  }
];
var LEVEL_BY_VALUE = new Map(
  DEFAULT_RISK_LEVELS.map((m) => [m.value, m.level])
);
function riskLevelForValue(value) {
  if (value === null || value === void 0) return null;
  return LEVEL_BY_VALUE.get(value) ?? null;
}
function riskRank(level) {
  return RISK_RANK[level];
}
function isHigherRisk(next, current) {
  return current === null || current === void 0 || next > current;
}
function riskFromReviewSeverities(severities) {
  if (severities.includes("critical")) return "critical";
  if (severities.includes("major")) return "high";
  return "medium";
}

// src/types/task-status.ts
var PR_PRODUCING_AGENT_MODES = ["building", "auto"];
function isPrProducingMode(mode) {
  return PR_PRODUCING_AGENT_MODES.includes(mode ?? "");
}
var CARD_TYPE_VALUES = ["task", "incident", "suggestion", "chat"];
var UNIFIED_STATUSES = [
  "Planning",
  "Open",
  "InProgress",
  "ReviewPR",
  "ReviewDev",
  "ReviewLive",
  "Complete",
  "Cancelled"
];
var CARD_TYPE_STATUSES = {
  task: UNIFIED_STATUSES,
  incident: UNIFIED_STATUSES,
  suggestion: UNIFIED_STATUSES,
  // Chat cards spawn a conversational cloud agent that never opens a PR — they
  // move InProgress → Complete directly. Reuse the unified set so the direct
  // Complete transition validates.
  chat: UNIFIED_STATUSES
};
function validateStatusForType(type, status) {
  return CARD_TYPE_STATUSES[type].includes(status);
}
var ACTIVE_WORK_STATUSES = [
  "InProgress",
  "ReviewPR",
  "ReviewDev",
  "ReviewLive",
  "Complete"
];
function isActiveWorkStatus(status) {
  return ACTIVE_WORK_STATUSES.includes(status);
}
var IDENTIFIED_WORK_STATUSES = ["Open", ...ACTIVE_WORK_STATUSES];
function isIdentifiedWorkStatus(status) {
  return IDENTIFIED_WORK_STATUSES.includes(status);
}
var HIDDEN_REPORT_STATUSES = ["Cancelled", "Rejected", "Duplicate"];
var REPORT_STATUS_TO_LANE = {
  Planning: "Planning",
  Open: "Open",
  Investigating: "InProgress",
  Accepted: "InProgress",
  InProgress: "InProgress",
  ReviewPR: "ReviewPR",
  ReviewDev: "ReviewDev",
  ReviewLive: "ReviewLive",
  Resolved: "Complete",
  Implemented: "Complete",
  Closed: "Complete",
  Complete: "Complete",
  Rejected: null,
  Duplicate: null,
  Cancelled: null
};
function normalizeReportStatus(status) {
  return REPORT_STATUS_TO_LANE[status] ?? null;
}
var LEGACY_TERMINAL_STATUSES = /* @__PURE__ */ new Set(["Rejected", "Duplicate"]);
function normalizeTaskStatus(status) {
  return LEGACY_TERMINAL_STATUSES.has(status) ? "Cancelled" : status;
}
var TASK_STATUS_COLORS = {
  Planning: "#9ca3af",
  Open: "#60a5fa",
  InProgress: "#fbbf24",
  ReviewPR: "#a78bfa",
  ReviewDev: "#939bf9",
  ReviewLive: "#4ade80",
  Complete: "#10b981",
  Cancelled: "#a8a29e"
};
var DEFAULT_TASK_STATUS_COLOR = "#a8a29e";
function taskStatusColor(status) {
  const direct = TASK_STATUS_COLORS[status];
  if (direct) return direct;
  const lane = normalizeReportStatus(status);
  return lane ? TASK_STATUS_COLORS[lane] : DEFAULT_TASK_STATUS_COLOR;
}
function taskStatusColorInt(status) {
  return Number.parseInt(taskStatusColor(status).slice(1), 16);
}
var CREATED_DESC_STATUSES = ["ReviewLive"];
function isCreatedDescStatus(status) {
  return CREATED_DESC_STATUSES.includes(status);
}

// src/types/file-upload-limits.ts
var MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
var MAX_FILE_TAGS = 5;
var MAX_FILE_TAG_LENGTH = 100;

// src/types/service-map.ts
var ALLOWED_FILE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/json",
  "text/csv",
  "text/plain",
  "text/markdown",
  "application/pdf"
];
var EMBED_THRESHOLD_IMAGES = 5 * 1024 * 1024;
var EMBED_THRESHOLD_TEXT = 2 * 1024 * 1024;
var EMBEDDABLE_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp"
];
var EMBEDDABLE_TEXT_TYPES = [
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/json"
];
function isEmbeddableFile(mimeType, fileSize) {
  if (EMBEDDABLE_IMAGE_TYPES.some((t) => mimeType === t)) {
    return fileSize <= EMBED_THRESHOLD_IMAGES;
  }
  if (EMBEDDABLE_TEXT_TYPES.some((t) => mimeType === t)) {
    return fileSize <= EMBED_THRESHOLD_TEXT;
  }
  return false;
}

// src/types/connection-state.ts
var IDLE_HEARTBEAT_MS = 90 * 1e3;
var STRANDED_RUNNER_STATUSES = /* @__PURE__ */ new Set(["stalled_heartbeat", "stranded", "crashed"]);
function deriveConnectionState(inputs, now = Date.now()) {
  const heartbeatAgeMs = inputs.lastHeartbeatAt ? Math.max(0, now - new Date(inputs.lastHeartbeatAt).getTime()) : null;
  if (inputs.deletionRequestedAt) {
    return { state: "pendingDeletion", heartbeatAgeMs };
  }
  const status = inputs.codespaceStatus;
  if (status === "Deleted") {
    return { state: "deleted", heartbeatAgeMs };
  }
  if (status === "Creating" || inputs.podStatus === "Creating") {
    return { state: "creating", heartbeatAgeMs };
  }
  if (inputs.agentRunnerStatus && STRANDED_RUNNER_STATUSES.has(inputs.agentRunnerStatus)) {
    return { state: "stranded", heartbeatAgeMs };
  }
  if (status === "Stopped" || inputs.podStatus === "Stopped" || inputs.podStatus === "Sleeping") {
    return { state: "stopped", heartbeatAgeMs };
  }
  if (status !== "Running" && !inputs.codespaceId && !inputs.podName) {
    return { state: "none", heartbeatAgeMs };
  }
  if (heartbeatAgeMs !== null && heartbeatAgeMs >= IDLE_HEARTBEAT_MS) {
    return { state: "idle", heartbeatAgeMs };
  }
  if (status === "Running") {
    return { state: "active", heartbeatAgeMs };
  }
  return { state: "none", heartbeatAgeMs };
}
function formatHeartbeatAge(ms) {
  if (ms === null) return "never";
  const sec = Math.floor(ms / 1e3);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

// src/types/agent-event-schema.ts
import { z as z3 } from "zod";
var TurnEndToolCallSchema = z3.object({
  tool: z3.string(),
  input: z3.string().optional(),
  output: z3.string().optional(),
  timestamp: z3.string().optional()
}).passthrough();
var KnownAgentEventSchema = z3.discriminatedUnion("type", [
  // ── Lifecycle / connection ────────────────────────────────────────────
  z3.object({
    type: z3.literal("connected"),
    sessionId: z3.string(),
    projectId: z3.string().optional()
  }).passthrough(),
  // Open-ended context snapshot spread from buildInitializationContext().
  z3.object({ type: z3.literal("session_manifest") }).passthrough(),
  z3.object({
    type: z3.literal("agent_runner_status"),
    reason: z3.string(),
    attempt: z3.number().optional(),
    attempts: z3.number().optional()
  }).passthrough(),
  z3.object({ type: z3.literal("shutdown"), reason: z3.string().optional() }).passthrough(),
  z3.object({ type: z3.literal("mode_changed"), agentMode: z3.string() }).passthrough(),
  z3.object({ type: z3.literal("mode_transition"), from: z3.string(), to: z3.string() }).passthrough(),
  // ── Turn stream ───────────────────────────────────────────────────────
  z3.object({ type: z3.literal("message"), content: z3.string() }).passthrough(),
  z3.object({ type: z3.literal("thinking"), message: z3.string() }).passthrough(),
  z3.object({
    type: z3.literal("tool_use"),
    tool: z3.string(),
    // Producers send JSON.stringify(input); consumers defend against
    // object inputs from older agents, so the wire stays permissive here.
    input: z3.unknown().optional()
  }).passthrough(),
  z3.object({
    type: z3.literal("tool_result"),
    tool: z3.string(),
    output: z3.unknown().optional(),
    isError: z3.boolean().optional(),
    redactedCount: z3.number().optional()
  }).passthrough(),
  z3.object({ type: z3.literal("turn_end"), toolCalls: z3.array(TurnEndToolCallSchema) }).passthrough(),
  z3.object({
    type: z3.literal("completed"),
    summary: z3.string().optional(),
    durationMs: z3.number().optional()
  }).passthrough(),
  z3.object({ type: z3.literal("error"), message: z3.string() }).passthrough(),
  z3.object({ type: z3.literal("agent_typing_start") }).passthrough(),
  z3.object({ type: z3.literal("agent_typing_stop") }).passthrough(),
  // ── Telemetry ─────────────────────────────────────────────────────────
  // heartbeat/typing: legacy telemetry the server still classifies as
  // transient (TRANSIENT_EVENT_TYPES) — kept in the vocabulary.
  z3.object({ type: z3.literal("heartbeat") }).passthrough(),
  z3.object({ type: z3.literal("typing") }).passthrough(),
  z3.object({
    type: z3.literal("context_update"),
    contextTokens: z3.number(),
    contextWindow: z3.number(),
    inputTokens: z3.number().optional(),
    cacheReadInputTokens: z3.number().optional(),
    cacheCreationInputTokens: z3.number().optional(),
    totalTokensUsed: z3.number().optional()
  }).passthrough(),
  // Three producer shapes share this type: {rateLimitType, utilization, status}
  // (SDK rate_limit_event), {resetsAt} (agent-connection resume notice), and
  // the usage-sampler ({rateLimitType, utilization, status, resetsAt, gauges}
  // — resetsAt matches rateLimitType; gauges survives via .passthrough()).
  z3.object({
    type: z3.literal("rate_limit_update"),
    rateLimitType: z3.string().optional(),
    utilization: z3.number().optional(),
    status: z3.string().optional(),
    resetsAt: z3.string().optional()
  }).passthrough(),
  z3.object({
    type: z3.literal("context_compacted"),
    trigger: z3.string().optional(),
    preTokens: z3.number().optional()
  }).passthrough(),
  z3.object({
    type: z3.literal("tool_progress"),
    toolName: z3.string().optional(),
    elapsedSeconds: z3.number().optional()
  }).passthrough(),
  z3.object({
    type: z3.literal("subagent_started"),
    sdkTaskId: z3.string().optional(),
    description: z3.string().optional()
  }).passthrough(),
  z3.object({
    type: z3.literal("subagent_progress"),
    sdkTaskId: z3.string().optional(),
    description: z3.string().optional(),
    toolUses: z3.number().optional(),
    durationMs: z3.number().optional()
  }).passthrough(),
  // ── Work products ─────────────────────────────────────────────────────
  z3.object({ type: z3.literal("pr_created"), url: z3.string(), number: z3.number() }).passthrough(),
  z3.object({
    type: z3.literal("code_review_complete"),
    result: z3.enum(["approved", "changes_requested"]),
    summary: z3.string().optional(),
    issues: z3.array(
      z3.object({
        file: z3.string(),
        line: z3.number().optional(),
        severity: z3.string().optional(),
        description: z3.string().optional()
      }).passthrough()
    ).optional()
  }).passthrough(),
  // ── Environment setup / start command ─────────────────────────────────
  z3.object({ type: z3.literal("setup_output"), stream: z3.string(), data: z3.string() }).passthrough(),
  z3.object({
    type: z3.literal("setup_complete"),
    startCommandRunning: z3.boolean().optional(),
    // Sanitized server-side by sanitizeSessionPreviewPorts — stays unknown.
    previewPorts: z3.unknown().optional()
  }).passthrough(),
  z3.object({ type: z3.literal("setup_error"), message: z3.string() }).passthrough(),
  z3.object({ type: z3.literal("start_command_started") }).passthrough(),
  z3.object({ type: z3.literal("start_command_output"), stream: z3.string(), data: z3.string() }).passthrough(),
  z3.object({
    type: z3.literal("start_command_exited"),
    code: z3.number().nullable().optional(),
    signal: z3.string().nullable().optional(),
    message: z3.string().optional()
  }).passthrough(),
  z3.object({ type: z3.literal("start_command_error"), message: z3.string() }).passthrough()
]);
var AgentEventSchema = z3.union([
  KnownAgentEventSchema,
  z3.object({ type: z3.string().min(1) }).catchall(z3.unknown())
]);

// src/types/agent-session-requests.ts
import { z as z4 } from "zod";
var cardDescription = z4.string().max(CARD_DESCRIPTION_MAX, CARD_DESCRIPTION_LIMIT_MESSAGE).optional();
var AgentHeartbeatSchema = z4.object({
  sessionId: z4.string().optional(),
  timestamp: z4.string(),
  status: z4.enum(["active", "idle", "building"]),
  currentAction: z4.string().optional(),
  /** Sender-observed main event-loop lag (ms) — see AgentHeartbeat.loopLagMs. */
  loopLagMs: z4.number().nonnegative().optional()
});
var CreatePRInputSchema = z4.object({
  title: z4.string().min(1),
  body: z4.string(),
  head: z4.string().optional(),
  base: z4.string().optional()
});
var PostToChatInputSchema = z4.object({
  message: z4.string().min(1),
  type: z4.enum(["message", "question", "update"]).optional().default("message"),
  milestone: z4.enum(["plan_ready", "implementation_complete", "blocked"]).optional()
});
var GetTaskContextRequestSchema = z4.object({
  sessionId: z4.string(),
  includeHistory: z4.boolean().optional().default(false)
});
var GetChatMessagesRequestSchema = z4.object({
  sessionId: z4.string(),
  limit: z4.number().int().positive().optional().default(50),
  offset: z4.number().int().nonnegative().optional().default(0),
  /** Task id or slug to read chat from. Omit for the session's own task. Only
   * the session's own task or one of its children resolves — anything else is
   * an error, never a silent fallback to the caller's own chat. */
  taskId: z4.string().optional()
});
var GetTaskFilesRequestSchema = z4.object({
  sessionId: z4.string()
});
var GetTaskFileRequestSchema = z4.object({
  sessionId: z4.string(),
  fileId: z4.string()
});
var GetTaskRequestSchema = z4.object({
  sessionId: z4.string(),
  taskSlugOrId: z4.string()
});
var GetCliHistoryRequestSchema = z4.object({
  sessionId: z4.string(),
  limit: z4.number().int().positive().optional().default(100),
  source: z4.enum(["agent", "application"]).optional(),
  /** Task id or slug to read logs from. Omit for the session's own task. Only
   * the session's own task or one of its children resolves — anything else is
   * an error, never a silent fallback to the caller's own logs. */
  taskId: z4.string().optional()
});
var ListSubtasksRequestSchema = z4.object({
  sessionId: z4.string(),
  /** "compact" returns the slim orchestration view (ListSubtasksCompactResponse)
   * with the pack build-slot picture; "full" (default — wire-compat with older
   * agents) returns the verbose SubtaskSummaryDTO[] including description/plan. */
  view: z4.enum(["compact", "full"]).optional()
});
var GetDependenciesRequestSchema = z4.object({
  sessionId: z4.string()
});
var GetSuggestionsRequestSchema = z4.object({
  sessionId: z4.string(),
  status: z4.string().optional(),
  limit: z4.number().int().min(1).max(100).optional()
});
var ListManualTestsRequestSchema = z4.object({
  sessionId: z4.string()
});
var QueryManualTestsRequestSchema = z4.object({
  sessionId: z4.string(),
  cardStatuses: z4.array(z4.string()).optional(),
  testStatuses: z4.array(z4.enum(["open", "approved", "rejected"])).optional()
});
var PostToChatRequestSchema = PostToChatInputSchema;
var CreatePullRequestRequestSchema = CreatePRInputSchema.extend({ sessionId: z4.string() });
var RequestFileUploadRequestSchema = z4.object({
  sessionId: z4.string(),
  fileName: z4.string().min(1).max(255),
  mimeType: z4.string().min(1).max(128),
  fileSize: z4.number().int().positive().max(MAX_FILE_SIZE_BYTES)
});
var ConfirmFileUploadRequestSchema = z4.object({
  sessionId: z4.string(),
  fileId: z4.string(),
  title: z4.string().max(500).optional(),
  /** Glossary tag names (or ids) this file is an example of. */
  tags: z4.array(z4.string().min(1).max(MAX_FILE_TAG_LENGTH)).max(MAX_FILE_TAGS).optional()
});
var UpdateTaskStatusRequestSchema = z4.object({
  sessionId: z4.string(),
  status: z4.string(),
  force: z4.boolean().optional().default(false)
});
var StoreSessionIdRequestSchema = z4.object({
  sessionId: z4.string(),
  sdkSessionId: z4.string()
});
var SetManualTestsRequestSchema = z4.object({
  sessionId: z4.string(),
  items: z4.array(z4.object({ title: z4.string().min(1) })).min(1)
});
var EditManualTestRequestSchema = z4.object({
  sessionId: z4.string(),
  title: z4.string().min(1),
  newTitle: z4.string().min(1)
});
var RemoveManualTestRequestSchema = z4.object({
  sessionId: z4.string(),
  title: z4.string().min(1)
});
var ApproveManualTestRequestSchema = z4.object({
  sessionId: z4.string(),
  title: z4.string().min(1)
});
var RejectManualTestRequestSchema = z4.object({
  sessionId: z4.string(),
  title: z4.string().min(1),
  reason: z4.string().min(1).max(2e3)
});
var HeartbeatRequestSchema = AgentHeartbeatSchema;
var SessionStartRequestSchema = z4.object({
  sessionId: z4.string(),
  agentVersion: z4.string(),
  capabilities: z4.array(z4.string())
});
var SessionStopRequestSchema = z4.object({
  sessionId: z4.string(),
  reason: z4.string().optional()
});
var EndReviewSessionRequestSchema = z4.object({
  sessionId: z4.string(),
  reason: z4.enum(["approved", "changes_requested", "finished"]).optional()
});
var ConnectAgentRequestSchema = z4.object({
  sessionId: z4.string()
});
var ReportAgentStatusRequestSchema = z4.object({
  sessionId: z4.string(),
  status: z4.string(),
  /** Why the agent reports this status (e.g. "user_question" while an AskUserQuestion questionnaire is pending in the TUI). */
  reason: z4.string().optional(),
  /**
   * The pending question text, sent only alongside `reason: "user_question"`
   * so the server can surface it in the user-question notification body (and
   * thus the Attention feed) instead of a generic string. Optional: older
   * agents omit it and the server falls back to the generic wording.
   */
  questionText: z4.string().optional()
});
var NotifyAgentVersionRequestSchema = z4.object({
  sessionId: z4.string(),
  agentVersion: z4.string()
});
var DiscoveredPortSchema = z4.object({
  port: z4.number().int().min(1).max(65535),
  label: z4.string().min(1).max(64).optional(),
  protocol: z4.enum(["http", "tcp"]).optional(),
  detectedAt: z4.string()
});
var ReportDiscoveredPortsRequestSchema = z4.object({
  sessionId: z4.string(),
  ports: z4.array(DiscoveredPortSchema).max(64)
});
var ReportBootMilestoneRequestSchema = z4.object({
  sessionId: z4.string(),
  key: z4.string().max(64)
});
var CreateSubtaskRequestSchema = z4.object({
  sessionId: z4.string(),
  title: z4.string().min(1),
  description: cardDescription,
  plan: z4.string().optional(),
  storyPointValue: z4.number().int().positive().optional(),
  ordinal: z4.number().int().nonnegative().optional(),
  followParentStatus: z4.boolean().optional(),
  /** Sibling subtask ids or slugs this subtask blocks on (explicit dependency
   * metadata — preferred over encoding order in plan text / ordinal). */
  dependsOn: z4.array(z4.string().min(1)).max(32).optional()
});
var UpdateSubtaskRequestSchema = z4.object({
  sessionId: z4.string(),
  subtaskId: z4.string(),
  title: z4.string().min(1).optional(),
  description: cardDescription,
  plan: z4.string().optional(),
  /** Orchestration statuses only ("Planning" | "Open") — the pack parent's
   * sanctioned promotion path. Execution statuses stay with the build
   * pipeline / force_update_task_status. Enforced server-side. */
  status: z4.string().optional(),
  /** Assign a project agent to the child — accepts the agent's id or exact
   * name; resolved against the parent task's project server-side. */
  agentIdOrName: z4.string().min(1).optional(),
  storyPointValue: z4.number().int().positive().optional(),
  followParentStatus: z4.boolean().optional(),
  /** Replace this subtask's dependency edges with these sibling ids/slugs.
   * Empty array clears all. Omit to leave dependencies unchanged. */
  dependsOn: z4.array(z4.string().min(1)).max(32).optional()
});
var DeleteSubtaskRequestSchema = z4.object({
  sessionId: z4.string(),
  subtaskId: z4.string()
});
var GetTaskPropertiesRequestSchema = z4.object({
  sessionId: z4.string()
});
var UpdateTaskFieldsRequestSchema = z4.object({
  sessionId: z4.string(),
  plan: z4.string().optional(),
  description: cardDescription
});
var UpdateTaskPropertiesRequestSchema = z4.object({
  sessionId: z4.string(),
  title: z4.string().optional(),
  storyPointValue: z4.number().int().positive().optional(),
  tagIds: z4.array(z4.string()).optional(),
  tagNames: z4.array(z4.string()).optional(),
  githubPRUrl: z4.string().url().optional(),
  githubBranch: z4.string().optional(),
  // Canonical risk level, or null to clear — same semantics as the headless
  // update_task boundary (resolved to the project's Risk row in the handler).
  risk: riskLevelSchema.nullable().optional()
});
var ListIconsRequestSchema = z4.object({
  sessionId: z4.string()
});
var GenerateTaskIconRequestSchema = z4.object({
  sessionId: z4.string(),
  prompt: z4.string().min(1),
  aspectRatio: z4.string().optional()
});
var SearchFaIconsRequestSchema = z4.object({
  sessionId: z4.string(),
  query: z4.string().min(1),
  first: z4.number().int().positive().optional()
});
var PickFaIconRequestSchema = z4.object({
  sessionId: z4.string(),
  fontAwesomeId: z4.string().min(1),
  fontAwesomeStyle: z4.string().optional()
});
var CreateFollowUpTaskRequestSchema = z4.object({
  sessionId: z4.string(),
  title: z4.string().min(1),
  description: cardDescription,
  plan: z4.string().optional(),
  storyPointValue: z4.number().int().positive().optional()
});
var AddDependencyRequestSchema = z4.object({
  sessionId: z4.string(),
  dependsOnSlugOrId: z4.string()
});
var RemoveDependencyRequestSchema = z4.object({
  sessionId: z4.string(),
  dependsOnSlugOrId: z4.string()
});
var CreateSuggestionRequestSchema = z4.object({
  sessionId: z4.string(),
  title: z4.string().min(1),
  description: cardDescription,
  tagNames: z4.array(z4.string()).optional()
});
var VoteSuggestionRequestSchema = z4.object({
  sessionId: z4.string(),
  suggestionId: z4.string(),
  value: z4.union([z4.literal(1), z4.literal(-1)])
});
var TriggerIdentificationRequestSchema = z4.object({
  sessionId: z4.string()
});
var HandoffToImplementerRequestSchema = z4.object({
  sessionId: z4.string(),
  // Optional difficulty sizing — sets the task's story points before resolving
  // the matched implementer agent. Omit to hand off using the task's current
  // story points (or the project's default task agent when unsized).
  storyPoints: z4.number().int().positive().optional(),
  // Optional kickoff note posted to the task chat alongside the handoff notice.
  message: z4.string().optional()
});
var SubmitCodeReviewResultRequestSchema = z4.object({
  sessionId: z4.string(),
  approved: z4.boolean(),
  content: z4.string(),
  // Canonical risk level the reviewer assigned to this change. Required on every
  // verdict — the reviewer must judge it. Applied authoritatively server-side
  // (may raise OR lower an already-set value; the reviewer has that authority).
  risk: riskLevelSchema,
  // The commit SHA the reviewer actually reviewed. When present, the verdict is
  // rejected unless the task is still at this SHA (guards against a late
  // old-SHA verdict overwriting a newer review cycle).
  reviewedSha: z4.string().optional()
});
var CycleCodingAgentKeyRequestSchema = z4.object({
  sessionId: z4.string(),
  rateLimitType: z4.string(),
  resetsAt: z4.string().optional()
});
var StartChildCloudBuildRequestSchema = z4.object({
  sessionId: z4.string(),
  childTaskId: z4.string()
});
var StopChildBuildRequestSchema = z4.object({
  sessionId: z4.string(),
  childTaskId: z4.string()
});
var ApproveAndMergePRRequestSchema = z4.object({
  sessionId: z4.string(),
  childTaskId: z4.string()
});
var PostChildChatMessageRequestSchema = z4.object({
  sessionId: z4.string(),
  childTaskId: z4.string(),
  message: z4.string().min(1)
});
var UpdateChildStatusRequestSchema = z4.object({
  sessionId: z4.string(),
  childTaskId: z4.string(),
  status: z4.string()
});
var GetAgentStatusRequestSchema = z4.object({
  taskId: z4.string()
});
var GetUiCliHistoryRequestSchema = z4.object({
  taskId: z4.string()
});
var GetActivePtySessionRequestSchema = z4.object({
  taskId: z4.string()
});
var ListActivePtySessionsRequestSchema = z4.object({
  taskId: z4.string()
});
var SendSoftStopRequestSchema = z4.object({
  taskId: z4.string()
});
var StopTaskSessionRequestSchema = z4.object({
  taskId: z4.string(),
  sessionId: z4.string()
});
var FlushTaskQueueRequestSchema = z4.object({
  taskId: z4.string(),
  softStop: z4.boolean().optional()
});
var CancelTaskQueuedMessageRequestSchema = z4.object({
  taskId: z4.string(),
  messageId: z4.string()
});
var FlushSingleQueuedMessageRequestSchema = z4.object({
  taskId: z4.string(),
  messageId: z4.string(),
  softStop: z4.boolean().optional()
});
var AnswerAgentQuestionRequestSchema = z4.object({
  taskId: z4.string(),
  requestId: z4.string(),
  answers: z4.record(z4.string(), z4.string())
});
var ClearAgentTodosRequestSchema = z4.object({
  taskId: z4.string()
});
var AgentQuestionOptionSchema = z4.object({
  label: z4.string(),
  description: z4.string(),
  preview: z4.string().optional()
});
var AgentQuestionSchema = z4.object({
  question: z4.string(),
  header: z4.string(),
  options: z4.array(AgentQuestionOptionSchema),
  multiSelect: z4.boolean().optional()
});
var AskUserQuestionRequestSchema = z4.object({
  sessionId: z4.string(),
  question: z4.string().min(1),
  requestId: z4.string().min(1),
  questions: z4.array(AgentQuestionSchema).min(1)
});
var PostAgentMessageRequestSchema = z4.object({
  sessionId: z4.string().min(1),
  content: z4.string(),
  milestone: z4.enum(["plan_ready", "implementation_complete", "blocked"]).optional()
});
var EmitAgentEventRequestSchema = z4.object({
  sessionId: z4.string(),
  events: z4.array(AgentEventSchema).max(500)
});
var RefreshGithubTokenRequestSchema = z4.object({
  sessionId: z4.string()
});
var ReportReviewSpawnFailureRequestSchema = z4.object({
  sessionId: z4.string(),
  reviewSessionId: z4.string(),
  error: z4.string().max(2e3).optional()
});
var RequestWorkspaceRecycleRequestSchema = z4.object({
  sessionId: z4.string(),
  reason: z4.string().max(2e3)
});
var SpawnTaskSessionRequestSchema = z4.object({
  taskId: z4.string(),
  kind: z4.enum(["tui", "shell"])
});
var StartCodeReviewRequestSchema = z4.object({
  taskId: z4.string(),
  force: z4.boolean().optional()
});
var StopCodeReviewRequestSchema = z4.object({
  taskId: z4.string()
});
var ReportSessionSpawnFailureRequestSchema = z4.object({
  sessionId: z4.string(),
  spawnedSessionId: z4.string(),
  error: z4.string().max(2e3).optional()
});
var RefreshGithubTokenResponseSchema = z4.object({
  token: z4.string()
});
var PTY_FRAME_MAX_CHARS = 256 * 1024;
var PTY_MAX_DIMENSION = 1e3;
var PtyOutputRequestSchema = z4.object({
  sessionId: z4.string(),
  data: z4.string().max(PTY_FRAME_MAX_CHARS),
  cols: z4.number().int().positive().max(PTY_MAX_DIMENSION).optional(),
  rows: z4.number().int().positive().max(PTY_MAX_DIMENSION).optional()
});
var PtyEndedRequestSchema = z4.object({
  sessionId: z4.string()
});
var PtyInputRequestSchema = z4.object({
  sessionId: z4.string(),
  data: z4.string().max(PTY_FRAME_MAX_CHARS)
});
var PtyResizeRequestSchema = z4.object({
  sessionId: z4.string(),
  cols: z4.number().int().positive().max(PTY_MAX_DIMENSION),
  rows: z4.number().int().positive().max(PTY_MAX_DIMENSION)
});
var PtyAttachRequestSchema = z4.object({
  sessionId: z4.string()
});
var ReportPtyStreamRequestSchema = z4.object({
  sessionId: z4.string(),
  port: z4.number().int().positive().max(65535).nullable()
});
var GetPtyStreamEndpointRequestSchema = z4.object({
  sessionId: z4.string()
});
var PtyChatEventPayloadSchema = z4.discriminatedUnion("kind", [
  z4.object({
    kind: z4.literal("init"),
    model: z4.string().max(200),
    claudeSessionId: z4.string().max(100).optional()
  }),
  z4.object({
    kind: z4.literal("user_text"),
    text: z4.string().max(16384),
    // Set by the SERVER (never the agent) when this prompt was injected by
    // Conveyor rather than typed by a human — the routed message's `source`
    // (`ci_success`, `review_trigger`, `automated_feedback`, …). The agent
    // pastes automated messages into the TUI exactly like human prompts, so the
    // CLI records both as plain transcript `user` records; without this the
    // builder chat renders "All CI checks passed on your PR." as the human's
    // own bubble. Absent ⇒ a genuine human prompt.
    source: z4.string().max(60).optional()
  }),
  z4.object({ kind: z4.literal("assistant_text"), text: z4.string().max(16384) }),
  z4.object({
    kind: z4.literal("tool_use"),
    name: z4.string().max(200),
    // Compact preview: JSON.stringify(input) truncated agent-side. The cap
    // matches the text events because AskUserQuestion payloads ride this field
    // and the web lifts them into an interactive card — a tight cap forced
    // option descriptions down to 80 chars, making them unreadable. Every
    // other tool keeps a far smaller agent-side budget (`TOOL_INPUT_MAX` in
    // `chat-record-mapper.ts`), so the ring does not grow for normal calls.
    input: z4.string().max(16384),
    // Transcript tool_use block id — lets the client pair the tool_result.
    id: z4.string().max(100).optional()
  }),
  z4.object({
    kind: z4.literal("tool_result"),
    // tool_use block id this result answers (absent on malformed records).
    toolUseId: z4.string().max(100).optional(),
    // Compact output preview, truncated agent-side.
    output: z4.string().max(2e3),
    isError: z4.boolean().optional()
  }),
  z4.object({ kind: z4.literal("turn_end") })
]);
var PtyChatEventRequestSchema = z4.object({
  sessionId: z4.string(),
  event: PtyChatEventPayloadSchema
});
var PtyChatAttachRequestSchema = z4.object({
  sessionId: z4.string()
});
var CreatePRResponseSchema = z4.object({
  prNumber: z4.number().int().positive(),
  prUrl: z4.string().url(),
  /** Advisory glossary-upkeep note derived from the PR's changed files matched
   *  against tag contextPaths — rendered into the tool result, never stored. */
  glossaryNote: z4.string().optional()
});
var PostToChatResponseSchema = z4.object({
  messageId: z4.string()
});
var UpdateTaskStatusResponseSchema = z4.object({
  taskId: z4.string(),
  status: z4.string()
});
var StoreSessionIdResponseSchema = z4.object({
  success: z4.boolean()
});
var HeartbeatResponseSchema = z4.object({
  acknowledged: z4.boolean()
});
var SessionStartResponseSchema = z4.object({
  sessionId: z4.string(),
  startedAt: z4.string()
});
var SessionStopResponseSchema = z4.object({
  sessionId: z4.string(),
  stoppedAt: z4.string()
});
var DeleteSubtaskResponseSchema = z4.object({
  deleted: z4.boolean()
});

// src/types/agent-session-project-requests.ts
import { z as z5 } from "zod";
var cardDescription2 = z5.string().max(CARD_DESCRIPTION_MAX, CARD_DESCRIPTION_LIMIT_MESSAGE).optional();
var ListAccessibleProjectsRequestSchema = z5.object({
  pageSize: z5.number().int().positive().max(100).optional().default(100)
});
var ListProjectTasksRequestSchema = z5.object({
  projectId: z5.string(),
  status: z5.string().optional(),
  // Card types to include. Omitted/empty → defaults to ["task"] in the handler
  // (mirrors searchProjectTasks) so listing doesn't surface incidents/suggestions
  // unless asked. Enum validation lives at the MCP tool layer.
  typeFilters: z5.array(z5.string()).optional(),
  assigneeId: z5.string().optional(),
  unassigned: z5.boolean().optional(),
  // Scope to a sub-project board when provided. Unlike the board layer's `?? null`
  // semantics, agents default to seeing the whole project when omitted.
  subProjectId: z5.string().nullable().optional(),
  limit: z5.number().int().positive().optional().default(50)
}).refine((p) => !(p.unassigned && p.assigneeId), {
  message: "Pass either assigneeId or unassigned, not both"
});
var GetProjectTaskRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string()
});
var SearchProjectTasksRequestSchema = z5.object({
  projectId: z5.string(),
  // Tag names, matched case-insensitively against the project glossary.
  tagNames: z5.array(z5.string()).optional(),
  // How to combine tagNames: "any" (default) = carries at least one,
  // "all" = carries every one.
  tagMatch: z5.enum(["any", "all"]).optional(),
  // Expand each named tag to its descendants in the tag DAG before matching,
  // so a parent tag sweeps its whole area. Default false.
  includeChildTags: z5.boolean().optional(),
  searchQuery: z5.string().optional(),
  statusFilters: z5.array(z5.string()).optional(),
  // Card types to include. Omitted/empty → defaults to ["task"] in the handler so
  // search doesn't surface incidents/suggestions unless asked. Enum validation lives
  // at the MCP tool layer (mirrors statusFilters).
  typeFilters: z5.array(z5.string()).optional(),
  assigneeId: z5.string().optional(),
  unassigned: z5.boolean().optional(),
  // Scope to a sub-project board when provided. Unlike the board layer's `?? null`
  // semantics, agents default to seeing the whole project when omitted.
  subProjectId: z5.string().nullable().optional(),
  limit: z5.number().int().positive().optional().default(20)
}).refine((p) => !(p.unassigned && p.assigneeId), {
  message: "Pass either assigneeId or unassigned, not both"
});
var ListProjectTagsRequestSchema = z5.object({
  projectId: z5.string()
});
var GetProjectTagRequestSchema = z5.object({
  projectId: z5.string(),
  /** Tag id or exact (case-insensitive) tag name. */
  tag: z5.string().min(1).max(100)
});
var GetProjectSummaryRequestSchema = z5.object({
  projectId: z5.string()
});
var GetProjectOnboardingStatusRequestSchema = z5.object({
  projectId: z5.string()
});
var GetProjectOnboardingStepRequestSchema = z5.object({
  projectId: z5.string()
});
var GetProjectConnectUrlsRequestSchema = z5.object({
  projectId: z5.string()
});
var conveyorCapabilitySchema = z5.enum([
  "read",
  "create",
  "update",
  "chat",
  "files",
  "build"
]);
var GetConnectionContextRequestSchema = z5.object({
  projectId: z5.string(),
  // Optional board scope (CONVEYOR_SUBPROJECT_ID). Validated to belong to the
  // project in the handler; an invalid/foreign id is reported, not silently
  // dropped, so a mis-scoped connection is never presented as board-specific.
  subProjectId: z5.string().nullable().optional()
});
var VerifyConnectionRequestSchema = z5.object({
  projectId: z5.string(),
  subProjectId: z5.string().nullable().optional(),
  intendedActions: z5.array(conveyorCapabilitySchema).optional()
});
var ListAccessibleSubprojectsRequestSchema = z5.object({
  projectId: z5.string()
});
var CreateProjectTaskRequestSchema = z5.object({
  projectId: z5.string(),
  title: z5.string().min(1),
  description: cardDescription2,
  plan: z5.string().optional(),
  status: z5.string().optional(),
  // Assign to a sub-project board. Validated to belong to `projectId` in the handler.
  subProjectId: z5.string().nullable().optional(),
  requestingUserId: z5.string().optional()
});
var UpdateProjectTaskRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  title: z5.string().optional(),
  description: cardDescription2,
  plan: z5.string().optional(),
  // Enum validation lives at the MCP tool layer (mirrors createProjectTask);
  // the handler routes through the shared updateStatus core (InProgress
  // dependency check + cleanup/board/Slack side effects), not the stricter
  // card-type-validating path the Socket.IO updateTaskStatus mutation uses.
  status: z5.string().optional(),
  // Canonical risk level, or null to clear. Resolved to the project's
  // configured Risk row (by rank) in the handler.
  risk: riskLevelSchema.nullable().optional(),
  // Story-point value, or null to clear. Resolved to the project's configured
  // StoryPoint row in the handler, which rejects an unconfigured value.
  storyPointValue: z5.number().int().positive().nullable().optional(),
  assignedUserId: z5.string().nullish(),
  // Move to a different sub-project board, or null to move to the parent board.
  // Validated to belong to `projectId` in the handler.
  subProjectId: z5.string().nullable().optional(),
  requestingUserId: z5.string().optional()
}).strict().refine(
  (v) => v.title !== void 0 || v.description !== void 0 || v.plan !== void 0 || v.status !== void 0 || v.risk !== void 0 || v.storyPointValue !== void 0 || v.assignedUserId !== void 0 || v.subProjectId !== void 0,
  {
    message: "update_task requires at least one field to change (title, description, plan, status, risk, storyPointValue, assignedUserId, or subProjectId)"
  }
);
var TransitionProjectTaskStatusRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  toStatus: z5.string(),
  expectedFromStatus: z5.string().optional(),
  // Optional raise-only risk to attempt alongside the transition
  // (approve → low, request_changes → medium by default).
  risk: riskLevelSchema.optional(),
  requestingUserId: z5.string().optional()
});
var MoveProjectCardRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  destinationProjectId: z5.string(),
  requestingUserId: z5.string().optional()
});
var PostToProjectTaskChatRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  content: z5.string(),
  requestingUserId: z5.string().optional()
});
var GetProjectTaskCliRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  limit: z5.number().int().positive().optional().default(50),
  source: z5.string().optional()
});
var GetProjectTaskSessionsRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string()
});
var QueryProjectGcpLogsRequestSchema = z5.object({
  projectId: z5.string(),
  env: z5.enum(["prod", "dev", "claudespace"]).optional(),
  severity: z5.enum(["DEBUG", "INFO", "NOTICE", "WARNING", "ERROR", "CRITICAL", "ALERT", "EMERGENCY"]).optional(),
  services: z5.array(z5.string().min(1).max(200)).max(25).optional(),
  sqlInstances: z5.array(z5.string().min(1).max(200)).max(25).optional(),
  allServices: z5.boolean().optional(),
  search: z5.string().max(256).optional(),
  filter: z5.string().max(1e3).optional(),
  startTime: z5.string().optional(),
  endTime: z5.string().optional(),
  limit: z5.number().int().min(1).max(200).optional().default(50),
  pageToken: z5.string().max(4096).optional()
});
var QueryProjectGrafanaLogsRequestSchema = z5.object({
  projectId: z5.string(),
  env: z5.enum(["prod", "dev"]).optional(),
  services: z5.array(z5.string().min(1).max(200)).max(25).optional(),
  level: z5.enum(["debug", "info", "warn", "error", "fatal"]).optional(),
  search: z5.string().max(256).optional(),
  logql: z5.string().max(2e3).optional(),
  startTime: z5.string().optional(),
  endTime: z5.string().optional(),
  limit: z5.number().int().min(1).max(200).optional().default(50)
});
var StartProjectBuildRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  requestingUserId: z5.string().optional()
});
var StopProjectBuildRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  requestingUserId: z5.string().optional()
});
var StartProjectWorkspaceRequestSchema = z5.object({
  projectId: z5.string(),
  requestingUserId: z5.string().optional()
});
var StopProjectWorkspaceRequestSchema = z5.object({
  projectId: z5.string(),
  destroy: z5.boolean().optional(),
  requestingUserId: z5.string().optional()
});
var ListMyLiveSessionsRequestSchema = z5.object({
  projectId: z5.string(),
  /** Admin-only: list another member's sessions instead of the caller's. */
  targetUserId: z5.string().optional()
});
var ListProjectSessionGroupsRequestSchema = z5.object({
  projectId: z5.string()
});
var ListMyLiveSessionsAcrossProjectsRequestSchema = z5.object({});
var GetProjectAvailableTuisRequestSchema = z5.object({
  projectId: z5.string()
});
var StartAdhocSessionRequestSchema = z5.object({
  projectId: z5.string(),
  label: z5.string().max(200).optional(),
  /** Coding-agent key to launch under — validated pick-time (ownership + TUI availability) in the handler. */
  codingAgentKeyId: z5.string().optional(),
  /** Model override (Claude model id) — overrides the launch key's own model. */
  model: z5.string().max(200).optional(),
  /**
   * Session role. Constrained: other task-less modes fall through to the pm
   * runner in the pod entrypoint, and "review" would crash without a task.
   */
  mode: z5.enum(["adhoc", "pm"]).optional(),
  /** Base branch to check out (defaults to the project's dev branch). */
  branch: z5.string().max(300).optional(),
  /**
   * Server-assembled instructions the pod's TUI auto-submits once on first boot
   * (headless kickoff). Used by the onboarding "Set it up for me" flow to seed a
   * setup-driver prompt; the session stays watchable/interactive in the Sessions
   * view. `ensureAdhocWorkspace` persists it and clears it after first submit.
   */
  initialPrompt: z5.string().max(2e4).optional(),
  requestingUserId: z5.string().optional()
});
var StopAdhocSessionRequestSchema = z5.object({
  projectId: z5.string(),
  workspaceId: z5.string(),
  destroy: z5.boolean().optional(),
  requestingUserId: z5.string().optional()
});
var ResumeAdhocSessionRequestSchema = z5.object({
  projectId: z5.string(),
  workspaceId: z5.string(),
  requestingUserId: z5.string().optional()
});
var RefreshCodingAgentKeyUsageRequestSchema = z5.object({
  projectId: z5.string(),
  keyId: z5.string().optional(),
  requestingUserId: z5.string().optional()
});
var ListKeysToProbeRequestSchema = z5.object({
  sessionId: z5.string()
});
var CreateProjectReleaseRequestSchema = z5.object({
  projectId: z5.string(),
  taskIds: z5.array(z5.string()).optional(),
  requestingUserId: z5.string().optional()
});
var AddTasksToProjectReleaseRequestSchema = z5.object({
  projectId: z5.string(),
  taskIds: z5.array(z5.string()).min(1),
  requestingUserId: z5.string().optional()
});
var ApproveProjectMergePRRequestSchema = z5.object({
  projectId: z5.string(),
  childTaskId: z5.string(),
  requestingUserId: z5.string().optional()
});
var ListProjectSubtasksRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string()
});
var CreateProjectSubtaskRequestSchema = z5.object({
  projectId: z5.string(),
  parentTaskId: z5.string(),
  title: z5.string().min(1),
  description: cardDescription2,
  plan: z5.string().optional(),
  ordinal: z5.number().int().nonnegative().optional(),
  storyPointValue: z5.number().int().positive().optional(),
  followParentStatus: z5.boolean().optional(),
  /** Sibling subtask ids or slugs this subtask blocks on (explicit dependency
   * metadata — preferred over encoding order in plan text / ordinal). */
  dependsOn: z5.array(z5.string().min(1)).max(32).optional(),
  requestingUserId: z5.string().optional()
});
var UpdateProjectSubtaskRequestSchema = z5.object({
  projectId: z5.string(),
  subtaskId: z5.string(),
  title: z5.string().optional(),
  description: cardDescription2,
  plan: z5.string().optional(),
  status: z5.string().optional(),
  ordinal: z5.number().int().nonnegative().optional(),
  storyPointValue: z5.number().int().positive().optional(),
  followParentStatus: z5.boolean().optional(),
  /** Replace-set of sibling subtask ids/slugs this subtask blocks on ([] clears).
   * Mirrors the in-pod updateSubtask semantics. */
  dependsOn: z5.array(z5.string().min(1)).max(32).optional(),
  requestingUserId: z5.string().optional()
});
var DeleteProjectSubtaskRequestSchema = z5.object({
  projectId: z5.string(),
  subtaskId: z5.string(),
  requestingUserId: z5.string().optional()
});
var GetProjectTaskChatRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  limit: z5.number().int().positive().optional().default(20)
});
var AddProjectTaskDependencyRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  dependsOnSlugOrId: z5.string(),
  requestingUserId: z5.string().optional()
});
var RemoveProjectTaskDependencyRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  dependsOnSlugOrId: z5.string(),
  requestingUserId: z5.string().optional()
});
var VoteProjectSuggestionRequestSchema = z5.object({
  projectId: z5.string(),
  suggestionId: z5.string(),
  value: z5.union([z5.literal(1), z5.literal(-1)]),
  requestingUserId: z5.string().optional()
});
var GetProjectTaskDependenciesRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string()
});
var ListProjectTaskFilesRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string()
});
var GetProjectAttachmentRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  fileId: z5.string(),
  /** Byte offset into text content (paging large logs/JSON). Default 0. */
  offset: z5.number().int().nonnegative().optional(),
  /** Max bytes of text content to return from `offset`. Server default applies. */
  maxBytes: z5.number().int().positive().optional()
});
var RequestProjectFileUploadRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  fileName: z5.string().min(1).max(255),
  mimeType: z5.string().min(1).max(128),
  fileSize: z5.number().int().positive().max(MAX_FILE_SIZE_BYTES),
  requestingUserId: z5.string().optional()
});
var ConfirmProjectFileUploadRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  fileId: z5.string(),
  /** When set, the attachment is also posted to the task chat with this text. */
  comment: z5.string().max(2e3).optional(),
  /** Glossary tag names (or ids) this file is an example of. */
  tags: z5.array(z5.string().min(1).max(MAX_FILE_TAG_LENGTH)).max(MAX_FILE_TAGS).optional(),
  requestingUserId: z5.string().optional()
});
var CreateProjectPullRequestRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  title: z5.string().min(1),
  body: z5.string(),
  head: z5.string().optional(),
  base: z5.string().optional(),
  requestingUserId: z5.string().optional()
});
var ListProjectMembersRequestSchema = z5.object({
  projectId: z5.string()
});
var AddProjectTaskReviewerRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  userId: z5.string(),
  requestingUserId: z5.string().optional()
});
var RemoveProjectTaskReviewerRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  userId: z5.string(),
  requestingUserId: z5.string().optional()
});
var ListProjectManualTestsRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string()
});
var QueryProjectManualTestsRequestSchema = z5.object({
  projectId: z5.string(),
  cardStatuses: z5.array(z5.string()).optional(),
  testStatuses: z5.array(z5.enum(["open", "approved", "rejected"])).optional()
});
var SetProjectManualTestsRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  items: z5.array(z5.object({ title: z5.string().min(1) })).min(1),
  requestingUserId: z5.string().optional()
});
var EditProjectManualTestRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  title: z5.string().min(1),
  newTitle: z5.string().min(1),
  requestingUserId: z5.string().optional()
});
var RemoveProjectManualTestRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  title: z5.string().min(1),
  requestingUserId: z5.string().optional()
});
var ApproveProjectManualTestRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  title: z5.string().min(1),
  requestingUserId: z5.string().optional()
});
var RejectProjectManualTestRequestSchema = z5.object({
  projectId: z5.string(),
  taskId: z5.string(),
  title: z5.string().min(1),
  reason: z5.string().min(1).max(2e3),
  requestingUserId: z5.string().optional()
});
var CreateProjectSuggestionRequestSchema = z5.object({
  projectId: z5.string(),
  title: z5.string().min(1),
  description: cardDescription2,
  tagNames: z5.array(z5.string()).optional(),
  requestingUserId: z5.string().optional()
});

// src/types/review-guide-types.ts
import { z as z6 } from "zod";
var SHA_PATTERN = /^[0-9a-f]{40}$/i;
var ReviewGuideFileReferenceSchema = z6.object({
  path: z6.string().min(1).max(500),
  startLine: z6.number().int().positive().max(1e6).optional(),
  endLine: z6.number().int().positive().max(1e6).optional(),
  hunkHeader: z6.string().min(1).max(300).optional()
}).strict().superRefine((value, ctx) => {
  if (value.endLine !== void 0 && value.startLine === void 0) {
    ctx.addIssue({
      code: "custom",
      path: ["startLine"],
      message: "startLine is required when endLine is set"
    });
  }
  if (value.startLine !== void 0 && value.endLine !== void 0 && value.endLine < value.startLine) {
    ctx.addIssue({
      code: "custom",
      path: ["endLine"],
      message: "endLine must be greater than or equal to startLine"
    });
  }
});
var ReviewGuideSectionSchema = z6.object({
  title: z6.string().min(1).max(160),
  explanation: z6.string().min(1).max(2e3),
  classification: z6.enum(["core", "supporting"]).optional(),
  files: z6.array(ReviewGuideFileReferenceSchema).min(1).max(20)
}).strict();
var ReviewGuideContentSchema = z6.object({
  overview: z6.string().min(1).max(3e3),
  sections: z6.array(ReviewGuideSectionSchema).min(1).max(12)
}).strict();
var PublishReviewGuideRequestSchema = ReviewGuideContentSchema.extend({
  sessionId: z6.string().min(1),
  reviewedSha: z6.string().regex(SHA_PATTERN, "reviewedSha must be a 40-character commit SHA")
}).strict();

// src/types/task-audit-requests.ts
import { z as z7 } from "zod";

// src/utils/context-path-core.ts
var CONTEXT_PATH_UNTRACKED_PREFIXES = [
  "node_modules/",
  ".git/",
  "dist/",
  "build/",
  ".next/"
];
function normalizeContextPath(rawPath) {
  return rawPath.trim().replace(/^\.\//, "").replace(/\/+$/, "");
}

// src/utils/context-link-verify.ts
var CONTEXT_LINK_LOCATOR_MAX = 300;
var TEST_TITLE = /\b(?:it|test|describe)(?:\.\w+)?\s*\(\s*(['"`])((?:(?!\1)[\s\S])*)\1/g;
function extractTestTitles(content) {
  return [...content.matchAll(TEST_TITLE)].map((m) => m[2]);
}
function isPlaceholderLocator(locator) {
  return locator.includes("<") || locator.includes(">");
}
function locatorMatchesContent(content, locatorType, locator) {
  if (locatorType === "code") return content.includes(locator);
  return extractTestTitles(content).some((title) => title.includes(locator));
}
function contextLinkCheckKey(link) {
  return [normalizeContextPath(link.path), link.locatorType ?? "", link.locator ?? ""].join("\n");
}
function judgeContextLink(link, evidence) {
  const path = normalizeContextPath(link.path);
  if (!path) return { status: "unchecked", detail: "empty path" };
  if (CONTEXT_PATH_UNTRACKED_PREFIXES.some((prefix) => path.startsWith(prefix))) {
    return { status: "unchecked", detail: "path is outside the tracked tree" };
  }
  if (link.type === "folder") {
    if (evidence.entryType === "tree") return { status: "ok" };
    if (evidence.entryType === "blob") return { status: "stale", detail: "expected a folder, found a file" };
    return { status: "stale", detail: "folder not found" };
  }
  if (evidence.entryType === void 0) return { status: "stale", detail: "file not found" };
  if (evidence.entryType === "tree") return { status: "stale", detail: "expected a file, found a folder" };
  const { locator, locatorType } = link;
  if (!locator || !locatorType) return { status: "ok" };
  if (isPlaceholderLocator(locator)) {
    return { status: "unchecked", detail: "placeholder locator (contains <>)" };
  }
  const content = evidence.content ?? null;
  if (content === null) {
    return { status: "unchecked", detail: "file content unavailable" };
  }
  if (locatorMatchesContent(content, locatorType, locator)) return { status: "ok" };
  return {
    status: "stale",
    detail: locatorType === "test" ? `no test titled "${locator}" (renamed/removed?)` : `no match for "${locator}" (renamed/removed?)`
  };
}
function linkNeedsContent(link) {
  return link.type !== "folder" && typeof link.locator === "string" && link.locator.length > 0 && !isPlaceholderLocator(link.locator);
}
function verificationForLink(link, checks) {
  const entry = checks?.results[contextLinkCheckKey(link)];
  if (!checks || !entry) return { status: "unchecked" };
  return {
    status: entry.status,
    ...entry.detail === void 0 ? {} : { detail: entry.detail },
    checkedAt: checks.checkedAt,
    ref: checks.ref,
    ...checks.sha === void 0 ? {} : { sha: checks.sha }
  };
}
function withLinkVerification(links, checks) {
  return links.map((link) => ({ ...link, verification: verificationForLink(link, checks) }));
}
function parseTagContextLink(entry) {
  if (typeof entry !== "object" || entry === null) return null;
  const { type, path, label, locator, locatorType } = entry;
  if (typeof path !== "string" || path.length === 0) return null;
  if (type !== "rule" && type !== "doc" && type !== "file" && type !== "folder") return null;
  return {
    type,
    path,
    ...typeof label === "string" ? { label } : {},
    ...typeof locator === "string" && locator.length > 0 ? { locator } : {},
    ...locatorType === "test" || locatorType === "code" ? { locatorType } : {}
  };
}
function parseTagContextLinks(value) {
  if (!Array.isArray(value)) return [];
  return value.map(parseTagContextLink).filter((link) => link !== null);
}
function parseContextPathChecks(value) {
  if (typeof value !== "object" || value === null) return null;
  const { checkedAt, ref, sha, results } = value;
  if (typeof checkedAt !== "string" || typeof ref !== "string") return null;
  if (typeof results !== "object" || results === null) return null;
  const parsed = {};
  for (const [key, entry] of Object.entries(results)) {
    if (typeof entry !== "object" || entry === null) continue;
    const { status, detail } = entry;
    if (status !== "ok" && status !== "stale" && status !== "unchecked") continue;
    parsed[key] = { status, ...typeof detail === "string" ? { detail } : {} };
  }
  return {
    checkedAt,
    ref,
    ...typeof sha === "string" ? { sha } : {},
    results: parsed
  };
}

// src/utils/tag-limits.ts
var TAG_DESCRIPTION_MAX = CARD_DESCRIPTION_MAX;
var TAG_OVERVIEW_MAX = 32e3;
var TAG_REASON_MAX = 500;

// src/types/task-audit-requests.ts
var ProjectTagContextPathSchema = z7.object({
  type: z7.enum(["rule", "doc", "file", "folder"]),
  path: z7.string().min(1).max(500),
  label: z7.string().max(100).optional(),
  /** Verified-link tether — text that must keep existing in the file. */
  locator: z7.string().min(1).max(CONTEXT_LINK_LOCATOR_MAX).regex(/^[^\r\n]*$/, "Locator cannot contain line breaks").optional(),
  /** test = must appear in a real test/describe title; code = any substring. */
  locatorType: z7.enum(["test", "code"]).optional()
}).refine((link) => link.locator === void 0 === (link.locatorType === void 0), {
  message: "locator and locatorType must be provided together"
}).refine((link) => link.locator === void 0 || link.type !== "folder", {
  message: "folder links cannot carry a locator"
});
var hexColor = z7.string().regex(/^#[0-9a-fA-F]{6}$/, "Expected #RRGGBB hex color");
var overviewPathSchema = z7.string().min(1).max(500).regex(/^[^\r\n]*$/, "Overview path cannot contain line breaks");
var CreateProjectTagRequestSchema = z7.object({
  projectId: z7.string(),
  name: z7.string().min(1).max(50),
  color: hexColor.optional(),
  description: z7.string().max(TAG_DESCRIPTION_MAX).optional(),
  overview: z7.string().max(TAG_OVERVIEW_MAX).optional(),
  /** Source the overview from this repo file (stored overview stays as the pending fallback). */
  overviewPath: overviewPathSchema.optional(),
  contextPaths: z7.array(ProjectTagContextPathSchema).max(20).optional(),
  /** Parents to link at create time (multi-parent DAG). */
  parentTagIds: z7.array(z7.string()).max(25).optional(),
  requestingUserId: z7.string().optional()
});
var UpdateProjectTagRequestSchema = z7.object({
  projectId: z7.string(),
  tagId: z7.string(),
  name: z7.string().min(1).max(50).optional(),
  color: hexColor.optional(),
  description: z7.string().max(TAG_DESCRIPTION_MAX).optional(),
  /** Full markdown glossary body; null clears it. Rejected while overviewPath is set. */
  overview: z7.string().max(TAG_OVERVIEW_MAX).nullable().optional(),
  /** Repo file to source the overview from; null clears back to the stored overview. */
  overviewPath: overviewPathSchema.nullable().optional(),
  /** Full replacement of the tag's context links when provided. */
  contextPaths: z7.array(ProjectTagContextPathSchema).max(20).optional(),
  /** Full-set replacement of the tag's parent tags (multi-parent DAG). */
  parentTagIds: z7.array(z7.string()).max(25).optional(),
  /** One-line revision provenance, recorded in the tag's history. */
  reason: z7.string().max(TAG_REASON_MAX).optional(),
  /** Card the caller was working in — stamped into the revision history. */
  taskId: z7.string().optional(),
  requestingUserId: z7.string().optional()
});
var PostToProjectChatRequestSchema = z7.object({
  projectId: z7.string(),
  content: z7.string().min(1).max(2e4),
  requestingUserId: z7.string().optional(),
  /** Marks the post so the server can persist it beyond chat (tag-audit summaries land in tag history). */
  kind: z7.enum(["tag_audit_summary"]).optional()
});
var StartTagAuditRequestSchema = z7.object({
  projectId: z7.string(),
  requestingUserId: z7.string().optional()
});
var StartTaskAuditRequestSchema = z7.object({
  projectId: z7.string(),
  taskIds: z7.array(z7.string()).min(1).max(20),
  requestingUserId: z7.string().optional()
});
var GetActiveAuditSessionsRequestSchema = z7.object({
  projectId: z7.string()
});
var ReportTaskAuditResultRequestSchema = z7.object({
  projectId: z7.string(),
  taskId: z7.string(),
  summary: z7.string(),
  turnGrades: z7.array(
    z7.object({
      turnIndex: z7.number(),
      phase: z7.enum(["planning", "building", "human"]),
      grade: z7.enum(["correct", "neutral", "blunder"]),
      reasoning: z7.string(),
      eventType: z7.string(),
      eventSummary: z7.string()
    })
  ),
  planningAccuracy: z7.number().nullable(),
  buildingAccuracy: z7.number().nullable(),
  humanAccuracy: z7.number().nullable(),
  planningCorrect: z7.number(),
  planningNeutral: z7.number(),
  planningBlunder: z7.number(),
  buildingCorrect: z7.number(),
  buildingNeutral: z7.number(),
  buildingBlunder: z7.number(),
  humanCorrect: z7.number(),
  humanNeutral: z7.number(),
  humanBlunder: z7.number(),
  humanEvaluations: z7.array(
    z7.object({
      messageIndex: z7.number(),
      rating: z7.union([z7.literal(-1), z7.literal(0), z7.literal(1)]),
      reasoning: z7.string()
    })
  ).optional(),
  suggestionIds: z7.array(z7.string()),
  auditCostUsd: z7.number().nullable(),
  model: z7.string().nullable(),
  /** When set, the audit is marked failed with this message instead. */
  error: z7.string().optional()
});
var GetTaskAuditsRequestSchema = z7.object({
  projectId: z7.string(),
  limit: z7.number().int().positive().max(200).optional().default(50)
});
var GetTaskAuditRequestSchema = z7.object({
  projectId: z7.string(),
  auditId: z7.string()
});
var GetTaskAuditAggregatesRequestSchema = z7.object({
  projectId: z7.string()
});
var DeleteTaskAuditRequestSchema = z7.object({
  projectId: z7.string(),
  auditId: z7.string(),
  requestingUserId: z7.string().optional()
});
var MarkInitialPromptSubmittedRequestSchema = z7.object({
  sessionId: z7.string()
});

// src/types/agent-session-events.ts
var CRITICAL_AUTOMATED_SOURCES = /* @__PURE__ */ new Set([
  "ci_failure",
  "review_trigger",
  "merge_conflict",
  "merge_failed",
  "pull_branch",
  // Child-task events for pack parents: the orchestrator must act (merge the
  // child's PR, start unblocked siblings, finish the pack) even after it
  // reported completed for a prior turn. Only ever sent to parent tasks.
  "parent"
]);
var createAgentSessionRoom = (sessionId) => `agentSession:${sessionId}`;
var createProjectRoom = (projectId) => `project:${projectId}`;

// src/types/workspace-v3.ts
var WORKSPACE_DESIRED_STATES = ["Running", "Sleeping", "Destroyed"];
var WORKSPACE_OBSERVED_STATES = [
  "Pending",
  "Provisioning",
  "Running",
  "Sleeping",
  "Destroying",
  "Destroyed",
  "Failed"
];
var WORKSPACE_PURPOSES = ["primary", "review", "project", "adhoc"];
var TASK_LESS_WORKSPACE_PURPOSES = [
  "project",
  "adhoc"
];
var WORKSPACE_POD_PHASES = [
  "Creating",
  "Ready",
  "Bound",
  "Suspended",
  "Terminating",
  "Gone",
  "Failed"
];
var WORKSPACE_BACKENDS = ["gke", "codespace"];
var WORKSPACE_SESSION_ROLES = ["writer", "reader"];
var WORKSPACE_SESSION_MODES = [
  "build",
  "review",
  "pm",
  "help",
  "adhoc",
  "pack",
  "shell"
];
var WORKSPACE_SESSION_STATUSES = ["Pending", "Active", "Stranded", "Ended"];
var MAX_DISCOVERED_PORTS = 16;
function sanitizeDiscoveredPorts(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const entry of raw) {
    if (out.length >= MAX_DISCOVERED_PORTS) break;
    if (!entry || typeof entry !== "object") continue;
    const port = entry.port;
    if (!isAllowablePreviewPort(port)) continue;
    if (seen.has(port)) continue;
    seen.add(port);
    const label = entry.label;
    const protocol = entry.protocol;
    const detectedAt = entry.detectedAt;
    out.push({
      port,
      ...typeof label === "string" && label.length > 0 ? { label } : {},
      ...protocol === "http" || protocol === "tcp" ? { protocol } : {},
      detectedAt: typeof detectedAt === "string" ? detectedAt : (/* @__PURE__ */ new Date(0)).toISOString()
    });
  }
  return out;
}

// src/types/repository-provider-types.ts
var REPOSITORY_PROVIDER_IDS = ["github", "forgejo"];
function normalizedUrl(value, field, protocols) {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`Forgejo ${field} is required`);
  }
  let parsed;
  try {
    parsed = new URL(value.trim());
  } catch {
    throw new Error(`Forgejo ${field} must be an absolute URL`);
  }
  if (!protocols.includes(parsed.protocol)) {
    throw new Error(`Forgejo ${field} must use ${protocols.join(" or ")}`);
  }
  if (parsed.username || parsed.password) {
    throw new Error(`Forgejo ${field} must not contain credentials`);
  }
  return parsed.toString().replace(/\/$/, "");
}
function resolveForgejoConnectionSettings(settings) {
  const forge = settings?.forge;
  const username = typeof forge?.username === "string" ? forge.username.trim() : "";
  if (!username) throw new Error("Forgejo username is required");
  const apiBase = normalizedUrl(forge?.apiBase, "apiBase", ["http:", "https:"]);
  if (!new URL(apiBase).pathname.endsWith("/api/v1")) {
    throw new Error("Forgejo apiBase must end with /api/v1");
  }
  return {
    provider: "forgejo",
    apiBase,
    cloneUrl: normalizedUrl(forge?.cloneUrl, "cloneUrl", ["http:", "https:"]),
    username
  };
}
function resolveForgeSettings(settings) {
  const forge = settings?.forge;
  if (!forge || forge.provider === void 0 || forge.provider === "github") {
    return { provider: "github" };
  }
  if (forge.provider !== "forgejo") {
    throw new Error("Unsupported repository provider");
  }
  return resolveForgejoConnectionSettings(settings);
}

// src/types/deployment-service-types.ts
var DEPLOYMENT_TARGET_PROVIDERS = [
  "pm2-local",
  "github-actions",
  "vercel",
  "cloud-run",
  "gke"
];
var DEPLOYMENT_TARGET_ENVIRONMENTS = ["dev", "prod", "staging", "custom"];

// src/generated/mcp-tool-registry.generated.ts
var MCP_TOOL_REGISTRY = [
  {
    name: "upload_attachment",
    description: "Upload a file (doc, notes, data, diagram, screenshot \u2014 any file type, up to 25MB) as a task attachment AND post it to the task chat in one step \u2014 no follow-up post_to_chat call needed. This is how you deliver a file the user should keep: it attaches to the card. Never publish deliverables as an external Claude artifact. Pass `tags` when the file is a good example of a glossary tag in some state.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/attachment-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "approve_manual_test",
    description: "Sign off on (approve) a manual test step on behalf of your authenticated user. Identify the test by its title (case-insensitive). Use after you have verified the step passes.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "edit_manual_test",
    description: "Rename an existing manual test step. Identify the test by its current title (case-insensitive); pass the new title to replace it. Use to correct or refine a recorded manual verification step.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "list_manual_tests",
    description: "List the manual test checklist items for the current task. Use to see what manual verification steps have already been recorded.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "query_manual_tests",
    description: "Query manual tests across many tasks in this project, grouped by task. Filter by card status (ReviewDev, ReviewLive, Complete, ...) and/or test status (open | approved | rejected). Use to answer 'show all OPEN manual tests in ReviewDev' or 'show all REJECTED manual tests with the failing reason'. With no filters it defaults to the needs-attention view: open+rejected tests on ReviewDev/ReviewLive cards.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "reject_manual_test",
    description: "Flag an issue with (reject) a manual test step on behalf of your authenticated user, recording the reason. Identify the test by its title (case-insensitive). Use when the step fails verification.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "remove_manual_test",
    description: "Remove an existing manual test step from the task checklist. Identify the test by its title (case-insensitive). Use to delete a stale or incorrect manual verification step.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "set_manual_tests",
    description: "Add manual test steps to the task checklist. Existing items with the same title are automatically skipped (deduplication). Use to record specific manual verification steps that reviewers should follow when testing this PR.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/checklist-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "approve_code_review",
    description: "Approve the code review and exit. Use when the diff passes all review criteria. Requires a summary and a risk level \u2014 for changes, use request_code_changes with a structured issues[] list.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/code-review-tools.ts",
    category: "codeReview",
    modes: "review"
  },
  {
    name: "request_code_changes",
    description: "Request changes during code review and exit. Use when substantive issues must be fixed before merge. Each issue: { file, line?, severity: critical|major|minor, description }.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/code-review-tools.ts",
    category: "codeReview",
    modes: "review"
  },
  {
    name: "get_dependencies",
    description: "Get this task's dependencies and their met/unmet status (met = merged to dev). Use to confirm blockers merged, or see why a task cannot start. For task state use get_task.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/dependency-suggestion-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "get_suggestions",
    description: "List project suggestions sorted by vote score. Filter by status or cap with limit (default 20). Suggestions are project-level ideas, not tasks \u2014 use get_task for tasks.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/dependency-suggestion-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "update_task_properties",
    description: "Set one or more task properties in a single call. Valid keys: title, storyPointValue, tagNames, githubPRUrl, githubBranch, risk. All are optional \u2014 include only the ones you want to update (at least one). Unknown keys are rejected.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/discovery-tools.ts",
    category: "discovery",
    modes: "discovery, auto"
  },
  {
    name: "add_dependency",
    description: "Add a blocking dependency \u2014 this task cannot start until the named task is merged to dev. For post-task follow-ups use create_follow_up_task instead.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "create_follow_up_task",
    description: "Create a follow-up task that depends on the current task. Use for out-of-scope work or cleanup that should land after this task merges. For blockers use add_dependency.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "create_pull_request",
    description: "Create a GitHub PR for this task. Auto-stages, commits (commitMessage or title default), pushes to origin, then opens the PR. Always use this instead of gh CLI or raw git.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "create_suggestion",
    description: "Suggest a feature, improvement, rule, or idea for the project. Duplicates are deduped and your upvote is recorded. For actionable work on this task open a follow-up task.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "force_update_task_status",
    description: "EMERGENCY ONLY: force-override a task's Kanban status. Use when an automatic transition failed and the task is wedged. Normal flow transitions status automatically.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all modes (emergency override; always exposed)"
  },
  {
    name: "post_to_chat",
    description: "Post a message to the task chat for the team to see. Your turn output is NOT shown in chat, so this is the only way the team sees your status, summaries, and questions. Omit task_id to post to the current task's chat; pass a child's ID to message its chat.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "remove_dependency",
    description: "Remove a previously added dependency from this task. When to use: the dependency was added in error or is no longer relevant. Returns: confirmation string.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "vote_suggestion",
    description: "Vote +1 or -1 on a project suggestion. Use to express support or disagreement with a specific suggestion returned by get_suggestions.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/mutation-tools.ts",
    category: "global",
    modes: "all PM/agent modes (common tools)"
  },
  {
    name: "approve_and_merge_pr",
    description: "Approve and merge a child task's PR. Preconditions: child in ReviewPR. Returns { merged }: true = merged (status\u2192ReviewDev); false = automerge queued, wait for ReviewDev. Requires project Admin, or a sub-project merge grant covering every changed file; release PRs require Admin.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "packRunner",
    modes: "parent task only (review, auto, discovery, help, building)"
  },
  {
    name: "create_subtask",
    description: "Create a subtask under the current parent task. Use when breaking a complex parent into smaller pieces during planning. For post-task follow-ups use create_follow_up_task.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "review, auto, discovery, help; building (parent task only). Pack tools (start_child_cloud_build, stop_child_build, approve_and_merge_pr) require parent task."
  },
  {
    name: "delete_subtask",
    description: "Delete a subtask by id. When to use: a subtask was created in error or is no longer needed. Returns: confirmation string.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "review, auto, discovery, help; building (parent task only). Pack tools (start_child_cloud_build, stop_child_build, approve_and_merge_pr) require parent task."
  },
  {
    name: "handoff_to_agent",
    description: "Hand this task off to an implementer agent for the build phase \u2014 mid-conversation, same session, no restart. Call this once the plan is compiled and saved (update_task_plan). The server swaps this task to the difficulty-sized implementer agent (which may run at a different model level), announces the handoff in the activity log + chat, and switches you into build mode to start implementing. Size the work with the storyPoints arg (or set it first via update_task_properties). Returns the implementer's name + model.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "review, auto, discovery, help; building (parent task only). Pack tools (start_child_cloud_build, stop_child_build, approve_and_merge_pr) require parent task."
  },
  {
    name: "list_subtasks",
    description: "List all subtasks under the current parent task. Default compact view returns per child: status, agent, story points, PR number/state, dependencies, and holdsBuildSlot \u2014 plus packSlots (in-flight environments vs the PACK_CHILD_LIMIT cap, and which children hold the slots). Use to coordinate child work; pass verbose:true only when you need full description/plan text (large). For non-child tasks use get_task.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "review, auto, discovery, help; building (parent task only). Pack tools (start_child_cloud_build, stop_child_build, approve_and_merge_pr) require parent task."
  },
  {
    name: "start_child_cloud_build",
    description: "Start a cloud build (codespace) for a child task. Preconditions: child status is `Open`, story points set, and an agent assigned \u2014 satisfy all three with update_subtask (status/agentIdOrName/storyPointValue) first; none happen automatically. A PACK_CHILD_LIMIT error is backpressure, not failure: check list_subtasks packSlots for which children hold the in-flight slots, merge/stop one, then retry. On a feature-branch pack, dev is merged into the pack branch first so the child branches from a fresh base; a conflict is reported in the result and never blocks the launch.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "packRunner",
    modes: "parent task only (review, auto, discovery, help, building)"
  },
  {
    name: "stop_child_build",
    description: "Send a graceful stop signal to a running child build's agent. Not a force-kill \u2014 the agent may take a moment to wind down. Stopping a child eventually frees its PACK_CHILD_LIMIT build slot (see list_subtasks packSlots).",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "packRunner",
    modes: "parent task only (review, auto, discovery, help, building)"
  },
  {
    name: "update_subtask",
    description: "Update an existing subtask's fields (title, description, plan, ordinal, storyPointValue, dependsOn) \u2014 and the sanctioned path to make a child buildable: promote it to status Open, assign its agent (agentIdOrName), and set story points. Setting story points does NOT auto-promote; set status explicitly. For the current task use update_task_plan.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "review, auto, discovery, help; building (parent task only). Pack tools (start_child_cloud_build, stop_child_build, approve_and_merge_pr) require parent task."
  },
  {
    name: "update_task_plan",
    description: "Save the plan and/or description to the current task. In auto/building mode, save the plan BEFORE writing code and keep it current as the approach evolves \u2014 post it, then build; never pause the build waiting for approval. For children use update_subtask; for title/tags/PR use update_task_properties.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/pm-tools.ts",
    category: "pm",
    modes: "task mode: discovery, auto only. PM/agent modes: review, auto, discovery, help; building (parent task only)."
  },
  {
    name: "create_suggestion",
    description: "File a project suggestion (idea/improvement for maintainers to review). Duplicates are AI-deduped into an existing suggestion with an upvote. Returns the suggestion id.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "create_tag",
    description: "Create a project tag. Include a crisp description (\u2264255 chars \u2014 the summary) and contextPaths (rule/doc/file/folder links agents auto-load when working on matching tasks); put the full spec in overview. Set parentTagIds to place the tag in the hierarchy right away. Every contextPath is checked against the repo checkout \u2014 a path that does not exist, or whose type does not match what is on disk, rejects the whole call. Fails if the name already exists.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "get_project_task",
    description: "Fetch any task in the project by id or slug: title, description, plan, status, and metadata. The audit evidence trail starts here.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "get_project_task_logs",
    description: "Read any project task's persisted agent event stream (message / tool_use / turn_end / error / completed). Turn boundaries are turn_end events. Entries are truncated to ~2KB each; max 500 per call.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "get_tag",
    description: "Read one tag's full glossary entry: description, the full markdown overview (the term's spec \u2014 philosophy, mechanics, invariants), linked files/rules (each with its verified-link status \u2014 ok/stale/unchecked \u2014 from the periodic repo check), parent/child tags, active-card count, and recent revisions with their reasons. Call this whenever a chat message, plan, or tag list points at a term you need the full context for. A response with `overviewPath` set means the overview is sourced from that repo file \u2014 prefer Reading the path from your checkout (branch-correct); the served overview is the base-branch materialization (`overviewSource.state`: ok/pending/stale).",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "all card sessions + headless task-less sessions"
  },
  {
    name: "list_tags",
    description: "List the project glossary: every tag's id, name, color, description, parent/child tag ids, its contextPaths (the rule/doc/file/folder links it wires into agent context \u2014 `[]` means none), and whether it carries a full overview (fetch that with get_tag). The context links ship inline, so you only need get_tag for a term's full overview. Use the ids with get_tag / update_tag.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "all card sessions + headless task-less sessions"
  },
  {
    name: "post_to_project_chat",
    description: "Post a markdown message to the PROJECT chat \u2014 use once at the end of an audit for the summary the team reads.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "read_project_task_chat",
    description: "Read any project task's chat messages (newest last). role 'user' rows are HUMAN turns; 'assistant'/'system' rows are agent posts and activity-log entries.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "report_task_audit_result",
    description: "Persist one audited task's grades (call once per task after grading it). Pass error instead to mark the audit failed when the evidence is unusable.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "headless task-less sessions (tag/task audits on ad-hoc pods)"
  },
  {
    name: "search_tasks",
    description: `Search this project's cards by tag, text, status, type, and/or assignment. Tags are the project's glossary, so tagNames is the fastest way to find prior work in the area you are touching \u2014 e.g. every ReviewPR card tagged "mcp". Searches the whole project, not just the current card. Defaults to type=task \u2014 pass typeFilters for incidents/suggestions. Every result lists the tags it carries, so you can see why it matched. Results are relevance-ordered: highest priority first then newest; suggestions-only queries rank by upvote score. Returns summaries \u2014 plan omitted, description truncated; use get_task for full details.`,
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "all card sessions + headless task-less sessions"
  },
  {
    name: "update_tag",
    description: "Update a tag's name, color, description (\u2264255), markdown overview, parent tags, or contextPaths. contextPaths and parentTagIds are FULL replacements \u2014 include what you want to keep. ALWAYS pass a short reason; it lands in the tag's revision history (with your current card auto-stamped) so the team sees why the glossary changed. Every contextPath is checked against the repo checkout \u2014 a path that does not exist, or whose type does not match what is on disk, rejects the whole call and nothing is written.",
    readOnly: false,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/project-tools.ts",
    category: "projectRunner",
    modes: "all card sessions + headless task-less sessions"
  },
  {
    name: "get_attachment",
    description: "Fetch one task file's content plus metadata by file ID. Call list_task_files first to discover IDs and check sizes \u2014 large binaries may be truncated by the service's size limit.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "get_current_plan",
    description: "Re-read the current task's plan. Use when the user updated the plan or asked you to re-read it \u2014 otherwise the plan is already in initial context. For task metadata use get_task.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "get_execution_logs",
    description: "Read CLI execution logs \u2014 agent reasoning, tool calls, and setup/dev-server output. Filter via source='agent' or 'application'. For human chat use read_task_chat.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "get_task",
    description: "Look up any task by slug or ID. Returns JSON with id, slug, title, description, plan, status, branch, githubPRNumber, githubPRUrl, storyPoints. For children use list_subtasks.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "list_task_files",
    description: "List all files attached to this task with metadata. Use before fetching a specific file to see what is available and how large each is. For file contents use get_attachment.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "read_task_chat",
    description: "Read recent human/user chat messages for a task. Omit task_id for the current task; pass a child ID for a child's chat. For agent logs use get_execution_logs.",
    readOnly: true,
    package: "conveyor-agent",
    sourceFile: "packages/conveyor-agent/src/tools/task-context-tools.ts",
    category: "global",
    modes: "all PM/agent modes (read-only common tools)"
  },
  {
    name: "get_attachment",
    description: "Fetch one task file's content plus metadata by file ID (accepts task id or slug). Pass projectId to target a specific project; otherwise the configured default project is used. Images are returned as viewable image blocks. Large text files (logs, JSON) are returned in pages \u2014 use `offset`/`maxBytes` to read more, or fetch `downloadUrl` for the whole file. Call list_task_files first to discover IDs and sizes.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/attachments.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_task_files",
    description: "List all files attached to a task with metadata (no contents \u2014 fast and small). Pass projectId to target a specific project; otherwise the configured default project is used. Use before fetching a specific file to see what is available and how large each is. For file contents use get_attachment.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/attachments.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "upload_attachment",
    description: "Upload a local file as a task attachment (any file type, up to 25MB). Pass projectId to target a specific project; otherwise the configured default project is used. The file appears under the task's Files. Pass `comment` to also post it to the task chat in the same step, and `tags` when the file is a good example of a glossary tag in some state.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/attachments.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "add_to_release",
    description: "Add Review (Dev) cards to the project's pending release \u2014 the same flow as the 'Add to Release' button in the web UI. Pass projectId to target a specific project; otherwise the configured default project is used. Cards must be in Review (Dev) and not already part of another release; the release branch is updated with the latest dev changes. Fails if no release is pending (use create_release instead).",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_release",
    description: "Create a release for the project \u2014 the same flow as the Release button in the web UI. Pass projectId to target a specific project; otherwise the configured default project is used. Creates a release task with a release/YYYY.MM.N branch and a PR from the dev branch to the default branch. Omit taskIds to release ALL cards currently in Review (Dev); pass a subset to cherry-pick \u2014 a cloud build agent then cherry-picks those changes and resolves conflicts. Fails if a release is already in progress or no cards are in Review (Dev).",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "delete_task_environment",
    description: "Delete a task environment, including durable Claudespace state. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_build_status",
    description: "Check codespace and agent status for a task. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "resume_task",
    description: "Resume a sleeping task Claudespace. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "sleep_task",
    description: "Sleep a task Claudespace, stopping compute while preserving durable state. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "start_task",
    description: "Start a cloud build (codespace) for a task. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "stop_task",
    description: "Compatibility alias for the legacy stop path. stop_task now performs the same durable sleep behavior as sleep_task, preserving task Claudespace state while stopping compute. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/builds.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "approve_manual_test",
    description: "Sign off on (approve) a manual test step on a task on behalf of your authenticated user. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its title (case-insensitive). Use after you have verified the step passes.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "edit_manual_test",
    description: "Rename an existing manual test step on a task. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its current title (case-insensitive) and pass the new title to replace it.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_manual_tests",
    description: "List the manual test checklist items for a task. Pass projectId to target a specific project; otherwise the configured default project is used. Use to see what manual verification steps have already been recorded.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "query_manual_tests",
    description: "Query manual tests across many tasks in a project, grouped by task. Filter by card status (e.g. ReviewDev, ReviewLive, Complete) and/or test status (open | approved | rejected). Use to answer questions like 'show all OPEN manual tests in ReviewDev' or 'show all REJECTED manual tests in ReviewDev/ReviewLive with the failing reason'. With no filters it defaults to the needs-attention view: open+rejected tests on ReviewDev/ReviewLive cards. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "reject_manual_test",
    description: "Flag an issue with (reject) a manual test step on a task on behalf of your authenticated user, recording the reason. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its title (case-insensitive). Use when the step fails verification.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "remove_manual_test",
    description: "Remove an existing manual test step from a task's checklist. Pass projectId to target a specific project; otherwise the configured default project is used. Identify the test by its title (case-insensitive).",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "set_manual_tests",
    description: "Add manual test steps to a task's checklist. Pass projectId to target a specific project; otherwise the configured default project is used. Existing items with the same title are automatically skipped (deduplication). Use to record specific manual verification steps that reviewers should follow when testing the task's PR.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/checklists.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_connection_context",
    description: "Resolve WHO this connection is and WHAT project/board it points at \u2014 call this FIRST, before inferring anything from names. Returns the effective account, project, and board (sub-project) each with BOTH its immutable ID and its human-readable name/slug, the granted capabilities (read/create/update/chat/files/build), management URLs, and a one-line summary. Removes the ambiguity between a project's canonical name and a board label. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/connection.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_accessible_subprojects",
    description: "List the boards (sub-projects) under the connected project \u2014 each with its ID, name, slug, board URL, owned root path, and the role/capabilities this token has on it. Use this to discover which board to create or list cards on (pass a returned id as subProjectId, or set CONVEYOR_SUBPROJECT_ID) instead of asking the human for one. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/connection.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "verify_connection",
    description: "Prove the connection is correct AND that you can actually write to the intended board \u2014 not just that auth works. Runs layered checks (auth \u2192 account \u2192 project \u2192 target board \u2192 capabilities \u2192 read) and returns a plain pass/fail with, on failure, the exact failing layer and ONE next action. Use this instead of get_project_summary to confirm setup: a summary that returns data proves auth, not scope. Pass intendedActions to verify specific capabilities (defaults to read+create+update). Pass projectId to target a specific project; otherwise the configured default project is used. The board scope comes from CONVEYOR_SUBPROJECT_ID.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/connection.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "add_dependency",
    description: "Add a blocking dependency \u2014 this task cannot start until the named task is merged to dev. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/dependencies.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_dependencies",
    description: "Get a task's dependencies and their met/unmet status (met = merged to dev). Pass projectId to target a specific project; otherwise the configured default project is used. Use to confirm blockers merged, or see why a task cannot start. For task state use get_task.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/dependencies.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "remove_dependency",
    description: "Remove a previously added dependency from a task. Pass projectId to target a specific project; otherwise the configured default project is used. The task is no longer blocked by the named task. Returns: confirmation string.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/dependencies.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "query_gcp_logs",
    description: "Query Google Cloud Logging for a project's linked GCP environments \u2014 use this to investigate production or dev issues directly ('something broke on prod'). Envs: 'prod' and 'dev' are the project's Cloud Run apps + Cloud SQL databases (scoped by default to the resources linked in project settings); 'claudespace' is the project's GKE agent-pod namespace. Start broad (severity=ERROR, sinceMinutes=60), then narrow with services/search. Returns compact lines: '<time> <SEVERITY> [<source>] <message> | key=value \u2026' (the key=value tail is the entry's structured payload \u2014 error details, service/method, actor and entity ids). When the response ends with a pageToken line, pass that token back as pageToken for the next page. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/logs.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "query_grafana_logs",
    description: "Query the project's connected Grafana (Loki) logs \u2014 the application logs shipped to Grafana Cloud/Loki, complementing query_gcp_logs (GCP infrastructure logs). Start with structured filters (env, level=error, sinceMinutes=60, services), then narrow with search; pass raw LogQL via logql only when structured filters can't express the query (it REPLACES them). The response header echoes the composed LogQL \u2014 iterate on it. Returns compact lines: '<time> <SEVERITY> [<service>] <message>'.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/logs.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_connect_urls",
    description: "Get browser-only setup URLs to hand to the user: the Google Cloud OAuth connect link (gcpConnect) plus Settings deep links (gcpSettings, memberSettings, projectSettings, setupWizard). Use these for setup steps an agent cannot perform (OAuth grants, secret entry). Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project-config.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_tag",
    description: "Read one tag's full glossary entry \u2014 description, markdown overview, context links (each with its verified-link status: ok/stale/unchecked plus last-checked provenance), parent/child tags, active-card count, and recent revisions with provenance. A response with `overviewPath` set means the overview is sourced from that repo file at the base branch (`overviewSource.state`: ok/pending/stale) \u2014 clients with a checkout can read the path directly for the branch-correct copy. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project-config.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "manage_priorities",
    description: "List, create, update, or delete project priorities. create requires value (1-100), name, and color; update/delete require the priority id. create/update require a Moderate role; delete requires Admin.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project-config.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "update_project_settings",
    description: "Update project configuration: name, description, default agent assignments, or a deep-merged patch of the project settings JSON (JSON Merge Patch: nested objects merge recursively, null deletes a key, arrays replace \u2014 a partial patch never wipes sibling keys). Requires a Moderate project role. Does NOT touch repositories or branches \u2014 those are configured in the Conveyor UI.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project-config.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_onboarding_status",
    description: "Get the project's onboarding/setup readiness checklist so you can drive setup to green without the user opening Project Settings. Returns { ok, checks[] } where each check has a key (githubApp, githubOauthScopes, claudeToken, devcontainer, devBranch, anthropicKey, compute, codespaceQuota), a status (ok | warn | fail), a human-readable reason, and an optional fix hint. Call this first during onboarding, resolve every 'fail' (use gh/gcloud CLIs or point the user at the fix), then call again until ok is true. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_onboarding_step",
    description: "Drive project setup one contextual step at a time (the choose-your-own-adventure onboarding flow). Returns { done, ok, step, remaining, checks }: `step` is the SINGLE next thing to configure (fails before warnings) with a human `title`, the `reason`, agent-facing `guidance` on how to resolve it, `autoFixable` (true = you can fix it via gh/git/tools, false = a browser-only step the user must do), an optional `fix` hint, and `connectUrls` (URLs to hand the user for browser-only steps). `remaining` lists the other unresolved checks. Prefer this over get_onboarding_status during setup: call it, resolve the returned step (do it yourself if autoFixable, otherwise give the user the connectUrl and wait), then call again \u2014 repeat until `done` is true. The repository and branches are already configured; never change them. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_project_summary",
    description: "Get overall project status: task counts by status, active builds, repo info. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_project_members",
    description: "List project members with user ID, name, email, and access level \u2014 use to resolve a person's name or email to a user ID for task assignment or review. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_projects",
    description: "List Conveyor projects available to this MCP token's user. Use a returned project id as projectId on other tools when no default project is configured or when targeting a different project.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/project.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_pull_request",
    description: "Open a GitHub pull request for a task's existing branch (the branch must already be pushed to origin). Pass projectId to target a specific project; otherwise the configured default project is used. Moves the task to ReviewPR. Returns the PR number and URL.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/pull-request.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_subtask",
    description: "Create a subtask under a parent task. Pass projectId to target a specific project; otherwise the configured default project is used. Subtasks break a larger task into independently buildable pieces. For children that instead ship on the parent's own branch/PR (e.g. per-theme tracking cards for one bundled PR), set followParentStatus so their status rides the parent's automatically.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/subtasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "delete_subtask",
    description: "Delete a subtask by ID. Pass projectId to target a specific project; otherwise the configured default project is used. This is permanent \u2014 use update_subtask to set status to Cancelled if you only want to close it.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/subtasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_subtasks",
    description: "List all subtasks of a parent task with their status and ordering. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/subtasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "update_subtask",
    description: "Update a subtask's fields: title, description, plan, status, ordering, story points, or dependencies. Pass projectId to target a specific project; otherwise the configured default project is used. Moving a subtask beyond Planning auto-fills missing story points and agent assignment \u2014 don't spend turns on them.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/subtasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_suggestion",
    description: "Suggest a feature, improvement, rule, or idea for the project. Pass projectId to target a specific project; otherwise the configured default project is used. Duplicates are deduped and your upvote is recorded.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/suggestions.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "add_reviewer",
    description: "Add a project member as a reviewer on a task. Pass projectId to target a specific project; otherwise the configured default project is used. Idempotent \u2014 adding an existing reviewer is a no-op.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "approve_and_merge_pr",
    description: "Approve a child task's pull request and QUEUE it for merge \u2014 the merge lands asynchronously (~30s sweep) once the CI and code-review gates pass; the response says whether it merged or was queued, so verify PR state before depending on it. Pass projectId to target a specific project; otherwise the configured default project is used. The child task must be in ReviewPR status with a PR. Requires project Admin, or a sub-project merge grant covering every changed file; release PRs require Admin.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "approve_task",
    description: "Move a task forward in the review flow (ReviewPR -> ReviewDev, or -> Complete). Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "create_task",
    description: "Create a new task with title, description, and optional plan. Pass projectId to target a specific project; otherwise the configured default project is used. Icon, story points, and agent assignment are auto-filled when a task is created in (or later moved to) a status beyond Planning \u2014 don't spend turns on them.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_card_by_slug",
    description: "Get full card details by the slug from a card URL (/cards/<slug>) instead of a task ID. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_task",
    description: "Get full task details including plan, chat history, PR info, subtasks, and build status. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "get_task_sessions",
    description: "Read compute-session state for a task: legacy CodespaceSession rows plus v3 workspaces, including purpose=review review workspaces. Shows pod identity, liveness, lifecycle, and code-review claim state. Use to diagnose stalled/dead agents or code-review runs. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "list_tags",
    description: "List all project tags with names, IDs, colors, descriptions, hierarchy (parent/child ids), contextPaths (the rule/doc/file/folder links each tag wires into agent context \u2014 `[]` means none), and a hasOverview flag. Context links ship inline; call get_tag only for a term's full overview. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "move_card",
    description: "Move an eligible Planning or Open task, incident, or suggestion card to another project. Cards with identification or automation in progress, active compute, pull requests, deployments, releases, reviews, or delivered reporter email cannot move. Project-specific metadata is cleared instead of mapped by name. Pass projectId for the source project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "post_to_chat",
    description: "Post a message to a task's chat. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "read_task_chat",
    description: "Read messages from a task's chat. Pass projectId to target a specific project; otherwise the configured default project is used. For agent execution logs use get_task_logs.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "remove_reviewer",
    description: "Remove a reviewer from a task. Pass projectId to target a specific project; otherwise the configured default project is used. Idempotent \u2014 removing a non-reviewer is a no-op.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "request_changes",
    description: "Post feedback and send task back to InProgress for more work. Pass projectId to target a specific project; otherwise the configured default project is used.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "search_tasks",
    description: "Search cards by tag name, text query, status, type, and/or assignment. Defaults to type=task \u2014 pass typeFilters to include incidents/suggestions. Pass projectId to target a specific project; otherwise the configured default project is used. Use tag names like 'agent-runner', not IDs. Every result lists the tags it carries, so you can see why it matched. Results are relevance-ordered: highest priority first then newest; suggestions-only queries rank by upvote score. Returns summaries \u2014 plan omitted, description truncated; use get_task for full details.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "update_task",
    description: "Update task fields: title, description, plan, status, risk, story points, assignment, or tags. Set status to claim a card (InProgress), triage it (Open), or cancel it (Cancelled); for review approvals prefer approve_task / request_changes, which guard against stale-state races. Tags are additive/subtractive \u2014 pass addTags/removeTags with tag names (not a replace-set). Pass projectId to target a specific project; otherwise the configured default project is used. Moving a task beyond Planning auto-fills any missing icon, story points, and agent assignment \u2014 don't spend turns on them; pass storyPointValue only to correct the sizing yourself. For subtasks use update_subtask.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/tasks.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "workspace_attach_info",
    description: "Return SSH/SFTP attach metadata for a running task Claudespace, plus hosted preview URLs and configured preview ports. Optionally installs an OpenSSH public key for this attach session.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/workspace.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "workspace_preview_urls",
    description: "Return the hosted preview URLs and preview ports for a running task Claudespace. This mirrors the web UI preview link metadata.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/workspace.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "workspace_start_tunnel",
    description: "Start a local loopback tunnel through the MCP server to a running task Claudespace port. Use port 2222 for SSH/SFTP, or one of previewPorts for app access.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/workspace.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  },
  {
    name: "workspace_stop_tunnel",
    description: "Stop a local workspace tunnel previously opened by workspace_start_tunnel.",
    readOnly: false,
    package: "conveyor-mcp",
    sourceFile: "packages/conveyor-mcp/src/tools/workspace.ts",
    category: "mcp",
    modes: "conveyor-mcp (MCP server)"
  }
];

// src/types/agent-tool-registry.ts
function getToolsByCategory(category) {
  return MCP_TOOL_REGISTRY.filter((tool) => tool.category === category);
}

// src/constants/timezone.ts
var PROJECT_TIMEZONE = "America/Los_Angeles";

// src/constants/agent-session-runner.ts
var CODESPACE_SESSION_ACTIVE_RUNNER_STATUSES = [
  "connected",
  "idle",
  "busy",
  "running",
  "fetching_context",
  "waiting_for_input",
  "setup",
  "connecting"
];
var TERMINAL_TASK_STATUSES = ["Complete", "Cancelled"];
var AGENT_STATUS_REASON_USER_QUESTION = "user_question";

// src/constants/agent-chat-history.ts
var TASK_CHAT_HISTORY_LIMIT = 20;
var PM_CHAT_HISTORY_LIMIT = 40;
var AGENT_CHAT_HISTORY_FETCH_LIMIT = Math.max(TASK_CHAT_HISTORY_LIMIT, PM_CHAT_HISTORY_LIMIT) + 10;

// src/constants/providers.ts
var MODEL_PROVIDERS = ["anthropic", "zen"];
var DEFAULT_MODEL_PROVIDER = "anthropic";
function isModelProvider(value) {
  return MODEL_PROVIDERS.includes(value);
}
function parseModelId(id) {
  const slash = id.indexOf("/");
  if (slash > 0) {
    const prefix = id.slice(0, slash);
    if (isModelProvider(prefix)) {
      return { provider: prefix, model: id.slice(slash + 1) };
    }
  }
  return { provider: DEFAULT_MODEL_PROVIDER, model: id };
}
function formatModelId(provider, model) {
  return `${provider}/${model}`;
}

// src/constants/model-catalog.ts
var MODEL_WIRE_FORMATS = [
  "anthropic-messages",
  "openai-chat",
  "openai-responses",
  "google-generate-content"
];
var SUPPORTED_WIRE_FORMATS = ["anthropic-messages", "openai-chat"];
function isSupportedWireFormat(format) {
  return SUPPORTED_WIRE_FORMATS.includes(format);
}
function anthropicEntry(model, label, inputPerMillion, outputPerMillion, experimental = false) {
  return {
    provider: "anthropic",
    model,
    id: formatModelId("anthropic", model),
    label,
    format: "anthropic-messages",
    inputPrice: inputPerMillion / 1e6,
    outputPrice: outputPerMillion / 1e6,
    supportsTools: true,
    ...experimental ? { experimental: true } : {}
  };
}
var ANTHROPIC_CATALOG = [
  anthropicEntry(DEFAULT_OPUS_MODEL, "Opus 5 Latest", 15, 75),
  anthropicEntry(PREVIOUS_OPUS_MODEL, "Opus 4.8", 15, 75),
  anthropicEntry(DEFAULT_SONNET_MODEL, "Sonnet 5 Latest", 3, 15),
  anthropicEntry(PREVIOUS_SONNET_MODEL, "Sonnet 4.6", 3, 15),
  anthropicEntry(DEFAULT_HAIKU_MODEL, "Haiku 4.5", 0.8, 4),
  anthropicEntry(FABLE_MODEL, "Fable 5 (experimental)", 10, 50, true)
];
function zenWireFormat(model) {
  const id = model.toLowerCase();
  if (id.startsWith("claude-") || id.startsWith("qwen")) return "anthropic-messages";
  if (id.startsWith("gpt-") || id.startsWith("grok-")) return "openai-responses";
  if (id.startsWith("gemini-")) return "google-generate-content";
  return "openai-chat";
}
function getCatalogModelLabel(id, catalog) {
  const { provider, model } = parseModelId(id);
  const entry = catalog.find((e) => e.provider === provider && e.model === model);
  return entry?.label ?? id;
}

// src/constants/workspace-tunnel.ts
var WORKSPACE_TUNNEL_HEARTBEAT_MS = 3e4;

// src/prompts/writing-style.ts
var HUMAN_PROSE_WRITING_STYLE = `## Writing style for humans
When you write prose a person will read \u2014 chat messages, plan updates, PR titles and bodies, PR review guides (the \`publish_review_guide\` overview and section explanations), review comments \u2014 follow these rules (based on ASD-STE100 Simplified Technical English):
- Use active voice. Say who does what ("The API rejects the request", not "the request is rejected").
- Use simple tenses ("we received", not "we have received").
- One instruction or fact per sentence. Keep sentences under ~20 words.
- Pick one word for one thing and reuse it. Do not rotate synonyms (check/verify/confirm) for the same action.
- Prefer the plain, common word ("use", not "utilize"; "start", not "initiate").
- Do not stack more than 3 nouns in a row ("task queue handler" is the limit).
- Use a numbered or bulleted list for 3+ steps or conditions instead of burying them in one sentence.
- Define a technical term on first use when a non-engineer will read the message.
- Never drop a condition, number, or scope qualifier to shorten a sentence. Precision beats brevity.
These rules do NOT apply to code, code comments, commit messages, or quoted output.`;

// src/room-helpers.ts
import { serviceRoom as coreServiceRoom, userRoom as coreUserRoom } from "@fitzzero/quickdraw-core";
function serviceRoom(service, entityId) {
  return coreServiceRoom(service, entityId);
}
function userRoom(userId) {
  return coreUserRoom(userId);
}

// src/admin-services.ts
var ADMIN_CREATE_SERVICES = /* @__PURE__ */ new Set([
  "agentService",
  "chatService",
  "documentService",
  "loginAllowlistService",
  "messageService",
  "projectService",
  "taskService",
  "userService"
]);
function adminSupportsCreate(serviceName) {
  return ADMIN_CREATE_SERVICES.has(serviceName);
}

// src/utils/access-levels.ts
var ACCESS_LEVEL_RANK = {
  Public: 0,
  Read: 1,
  Moderate: 2,
  Admin: 3
};
function accessLevelRank(level) {
  return level ? ACCESS_LEVEL_RANK[level] ?? 0 : 0;
}
function canGrantLevel(actorLevel, targetLevel) {
  return accessLevelRank(actorLevel) >= accessLevelRank(targetLevel);
}
function canModifyMember(actorLevel, memberLevel) {
  const actor = accessLevelRank(actorLevel);
  return actor >= ACCESS_LEVEL_RANK.Admin || actor > accessLevelRank(memberLevel);
}

// src/utils/xp.ts
var SP_XP_RATE = 10;
var REPORT_XP_RATE = 10;
var REVIEW_XP_RATE = 5;
var MAX_LEVEL = 99;
function xpToReachLevel(level) {
  if (level <= 1) return 0;
  return 5 * level * (level - 1);
}
function computeTotalXp({ spValueSum, reportCount, reviewCount }) {
  const spXp = spValueSum * SP_XP_RATE;
  const reportXp = reportCount * REPORT_XP_RATE;
  const reviewXp = reviewCount * REVIEW_XP_RATE;
  return { spXp, reportXp, reviewXp, totalXp: spXp + reportXp + reviewXp };
}
function levelFromXp(totalXp) {
  const xp = Number.isFinite(totalXp) && totalXp > 0 ? Math.floor(totalXp) : 0;
  let level = Math.floor((5 + Math.sqrt(25 + 20 * xp)) / 10);
  while (xpToReachLevel(level + 1) <= xp) level++;
  while (level > 1 && xpToReachLevel(level) > xp) level--;
  level = Math.min(MAX_LEVEL, Math.max(1, level));
  if (level >= MAX_LEVEL) {
    return {
      level: MAX_LEVEL,
      xpIntoLevel: xp - xpToReachLevel(MAX_LEVEL),
      xpForNextLevel: 0,
      progress: 1
    };
  }
  const floor = xpToReachLevel(level);
  const xpForNextLevel = xpToReachLevel(level + 1) - floor;
  const xpIntoLevel = xp - floor;
  const progress = xpForNextLevel === 0 ? 1 : Math.min(1, Math.max(0, xpIntoLevel / xpForNextLevel));
  return { level, xpIntoLevel, xpForNextLevel, progress };
}

// src/utils/merge-levels.ts
var MIN_CONTRIBUTOR_LEVEL = 1;
function toContributorLevel(value) {
  if (value === null || value === void 0) return null;
  if (!Number.isFinite(value)) return null;
  const level = Math.floor(value);
  if (level <= MIN_CONTRIBUTOR_LEVEL) return null;
  return Math.min(MAX_LEVEL, level);
}
function requiredContributorLevel(storyPointMin, riskMin) {
  const sp = toContributorLevel(storyPointMin);
  const risk = toContributorLevel(riskMin);
  if (sp === null) return risk;
  if (risk === null) return sp;
  return Math.max(sp, risk);
}
function meetsContributorLevel(actorLevel, required) {
  if (required === null) return true;
  if (actorLevel === null || actorLevel === void 0 || !Number.isFinite(actorLevel)) return false;
  return actorLevel >= required;
}

// src/utils/stat-date.ts
function todayInProjectTZ() {
  return (/* @__PURE__ */ new Date()).toLocaleDateString("en-CA", { timeZone: PROJECT_TIMEZONE });
}
function formatDateInProjectTZ(date) {
  return date.toLocaleDateString("en-CA", { timeZone: PROJECT_TIMEZONE });
}
function resolveCustomRange(startDate, endDate, today) {
  const [lo, hi] = startDate <= endDate ? [startDate, endDate] : [endDate, startDate];
  return { sinceDate: lo, untilDate: hi < today ? hi : today };
}
function resolveStatDateRange(statDate, fallbackDays) {
  const today = todayInProjectTZ();
  if (statDate) {
    if (statDate.mode === "custom" && statDate.startDate && statDate.endDate) {
      return resolveCustomRange(statDate.startDate, statDate.endDate, today);
    }
    if (statDate.mode === "day" && typeof statDate.month === "number" && typeof statDate.day === "number") {
      const dayStr = `${statDate.year}-${String(statDate.month).padStart(2, "0")}-${String(statDate.day).padStart(2, "0")}`;
      const clamped = dayStr < today ? dayStr : today;
      return { sinceDate: clamped, untilDate: clamped };
    }
    if (statDate.mode === "month" && typeof statDate.month === "number") {
      const sinceDate3 = `${statDate.year}-${String(statDate.month).padStart(2, "0")}-01`;
      const lastDay = new Date(statDate.year, statDate.month, 0).getDate();
      const endStr2 = `${statDate.year}-${String(statDate.month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      return { sinceDate: sinceDate3, untilDate: endStr2 < today ? endStr2 : today };
    }
    const sinceDate2 = `${statDate.year}-01-01`;
    const endStr = `${statDate.year}-12-31`;
    return { sinceDate: sinceDate2, untilDate: endStr < today ? endStr : today };
  }
  const days = fallbackDays ?? 30;
  const d = /* @__PURE__ */ new Date();
  d.setDate(d.getDate() - days);
  const sinceDate = formatDateInProjectTZ(d);
  return { sinceDate, untilDate: today };
}
var DAILY_MAX_DAYS = 31;
var MS_PER_DAY = 24 * 60 * 60 * 1e3;
function utcFromIso(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}
function isoFromUtc(ms) {
  const d = new Date(ms);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function resolveStatGranularity(sinceDate, untilDate) {
  if (sinceDate === untilDate) return "hour";
  const days = Math.round((utcFromIso(untilDate) - utcFromIso(sinceDate)) / MS_PER_DAY) + 1;
  return days <= DAILY_MAX_DAYS ? "day" : "week";
}
function isHourlyRange(sinceDate, untilDate) {
  return resolveStatGranularity(sinceDate, untilDate) === "hour";
}
function weekStartKey(dateStr) {
  const ms = utcFromIso(dateStr);
  const mondayOffset = (new Date(ms).getUTCDay() + 6) % 7;
  return `${isoFromUtc(ms - mondayOffset * MS_PER_DAY)}/W`;
}
function bucketDateKey(dateStr, granularity) {
  return granularity === "week" ? weekStartKey(dateStr) : dateStr;
}
function generateHourRange(dateStr) {
  const hours = [];
  for (let h = 0; h < 24; h++) {
    hours.push(`${dateStr} ${String(h).padStart(2, "0")}:00`);
  }
  return hours;
}
function generateDateRange(sinceStr, untilStr) {
  const dates = [];
  let current = utcFromIso(sinceStr);
  const end = utcFromIso(untilStr);
  while (current <= end) {
    dates.push(isoFromUtc(current));
    current += MS_PER_DAY;
  }
  return dates;
}
function generateWeekRange(sinceStr, untilStr) {
  const weeks = [];
  const mondayOffset = (new Date(utcFromIso(sinceStr)).getUTCDay() + 6) % 7;
  let current = utcFromIso(sinceStr) - mondayOffset * MS_PER_DAY;
  const end = utcFromIso(untilStr);
  while (current <= end) {
    weeks.push(`${isoFromUtc(current)}/W`);
    current += 7 * MS_PER_DAY;
  }
  return weeks;
}
function generateBucketRange(sinceStr, untilStr, granularity) {
  if (granularity === "hour") return generateHourRange(sinceStr);
  if (granularity === "week") return generateWeekRange(sinceStr, untilStr);
  return generateDateRange(sinceStr, untilStr);
}

// src/utils/message-filter.ts
var ACTIVITY_KIND_FILTERS = [
  "inputs",
  "milestones",
  "lifecycle",
  "external",
  "tools"
];
var KIND_CHIP = {
  human_input: "inputs",
  agent_milestone: "milestones",
  detail: "milestones",
  lifecycle: "lifecycle",
  external: "external"
};
var SPECIAL_CARD_METADATA_TYPES = /* @__PURE__ */ new Set([
  "activity_block",
  "activity_log",
  "codespace_boot_progress",
  "agent_question",
  "reporter_email_draft"
]);
function effectiveMessageKind(msg) {
  const kind = msg.kind ?? "chat";
  if (kind !== "chat") return kind;
  const metaType = msg.metadata?.type;
  if (metaType === "agent_mention") return "external";
  if (metaType && SPECIAL_CARD_METADATA_TYPES.has(metaType)) return "chat";
  if (metaType === "agent_response" || msg.role === "assistant") return "detail";
  return "chat";
}
function legacyChatChip(msg) {
  if (msg.metadata?.type === "activity_block") return "tools";
  if (msg.metadata?.type === "activity_log") return "lifecycle";
  if (msg.metadata?.type === "system_notification") return "external";
  if (msg.role === "user") return "inputs";
  return "milestones";
}
function messageMatchesActivityFilters(msg, filters) {
  if (msg.parentMessageId) return true;
  const chip = KIND_CHIP[effectiveMessageKind(msg)] ?? legacyChatChip(msg);
  return filters.includes(chip);
}

// src/utils/mentions.ts
var MENTION_TOKEN_REGEX = /@\[(\w+):([^\]]+)\]/g;
function serializeMention(type, id) {
  return `@[${type}:${id}]`;
}
function buildMentionKey(type, id) {
  return `${type}:${id}`;
}
function parseMentions(content) {
  const tokens = [];
  for (const match of content.matchAll(MENTION_TOKEN_REGEX)) {
    tokens.push({ type: match[1], id: match[2] });
  }
  return tokens;
}
function hasMentionToken(content) {
  return content.includes("@[");
}
function replaceMentionTokens(content, resolve) {
  if (!hasMentionToken(content)) return content;
  return content.replace(MENTION_TOKEN_REGEX, (raw, type, id) => {
    const hit = resolve(type, id);
    if (!hit) return raw;
    if (hit.slug) return `#${hit.slug}`;
    return hit.label ? `@${hit.label}` : raw;
  });
}
function stripMentionTokens(content) {
  if (!hasMentionToken(content)) return content;
  return content.replace(MENTION_TOKEN_REGEX, "").replace(/\s{2,}/g, " ").trim();
}
var BUILDER_STREAM_ID = "builder";
var REVIEWER_STREAM_ID = "reviewer";
var STREAM_MENTION_IDS = /* @__PURE__ */ new Set([BUILDER_STREAM_ID, REVIEWER_STREAM_ID]);
function getStreamMentionTarget(tokens) {
  const hit = tokens.find((t) => t.type === "agent" && STREAM_MENTION_IDS.has(t.id));
  return hit ? hit.id : null;
}
function getStreamMentionTargetFromKeys(keys) {
  for (const key of keys) {
    if (key === buildMentionKey("agent", BUILDER_STREAM_ID)) return BUILDER_STREAM_ID;
    if (key === buildMentionKey("agent", REVIEWER_STREAM_ID)) return REVIEWER_STREAM_ID;
  }
  return null;
}
var STREAM_MENTION_STRIP_REGEX = /(?:@\[agent:(?:builder|reviewer)\]|@\S+ \(agent:(?:builder|reviewer)\))[ \t]*/g;
function stripStreamMentionText(content) {
  return content.replace(STREAM_MENTION_STRIP_REGEX, "").trim();
}
function resolveMentions(content, metadata) {
  if (!metadata || typeof metadata !== "object") return content;
  const meta = metadata;
  if (meta.type !== "mentions" || !meta.mentions) return content;
  const mentions = meta.mentions;
  return content.replace(MENTION_TOKEN_REGEX, (_match, type, id) => {
    const key = `${type}:${id}`;
    const mention = mentions[key];
    if (!mention) return _match;
    const prefix = mention.slug ? `#${mention.slug}` : `@${mention.label}`;
    return `${prefix} (${type}:${id})`;
  });
}

// src/utils/tag-mentions.ts
function hasTagMentionToken(content) {
  return content.includes("@[tag:");
}
function indexTags(tags) {
  const byId = /* @__PURE__ */ new Map();
  const byName = /* @__PURE__ */ new Map();
  for (const tag of tags) {
    byId.set(tag.id, tag);
    const key = tag.name.trim().toLowerCase();
    byName.set(key, byName.has(key) ? null : tag);
  }
  return { byId, byName };
}
function toMentionData(tag) {
  return {
    type: "tag",
    label: tag.name,
    ...tag.color ? { color: tag.color } : {},
    ...tag.description ? { description: tag.description } : {},
    // Breadcrumb for external readers: a full spec exists, call get_tag for it.
    ...(tag.overview ?? "").trim().length > 0 ? { hasOverview: true } : {}
  };
}
function canonicalizeTagMentions(content, tags, redirects) {
  if (!hasTagMentionToken(content) || tags.length === 0 && !redirects?.size) {
    return { content, mentions: {}, rewritten: false };
  }
  const { byId, byName } = indexTags(tags);
  const mentions = {};
  let rewritten = false;
  const next = content.replace(MENTION_TOKEN_REGEX, (match, type, ref) => {
    if (type !== "tag") return match;
    const key = ref.trim().toLowerCase();
    const tag = byId.get(ref) ?? (byName.has(key) ? byName.get(key) : redirects?.get(key)) ?? null;
    if (!tag) return match;
    mentions[buildMentionKey("tag", tag.id)] = toMentionData(tag);
    const token = serializeMention("tag", tag.id);
    if (token !== match) rewritten = true;
    return token;
  });
  return { content: next, mentions, rewritten };
}

// src/utils/agent-triggers.ts
var FORCE_PREFIX = "!";
function hasForcePrefix(content) {
  return content.trimStart().startsWith(FORCE_PREFIX);
}
function stripForcePrefix(content) {
  const stripped = content.trimStart().slice(FORCE_PREFIX.length).trimStart();
  return stripped || content;
}
function resolveAgentTrigger(rawContent, opts) {
  const streamTarget = getStreamMentionTarget(parseMentions(rawContent));
  if (streamTarget) {
    return {
      target: streamTarget,
      deliveredContent: stripStreamMentionText(rawContent) || rawContent
    };
  }
  if (hasForcePrefix(rawContent)) {
    const stripped = rawContent.trimStart().slice(FORCE_PREFIX.length).trim();
    if (stripped || opts?.hasAttachments) {
      return { target: "builder", deliveredContent: stripped || rawContent };
    }
  }
  return { target: null, deliveredContent: rawContent };
}
function isAwaitingUserInput(source) {
  if (!source) return false;
  return !!source.awaitingUserInput || source.agentRunnerStatus === "waiting_for_input";
}

// src/utils/autopilot-ratio.ts
var AUTOPILOT_RATIO_MIN = 1;
var AUTOPILOT_RATIO_MAX = 6.5;
function computeMemoryBounds(agentCpu, sidecarCpu, sidecarMemoryGi) {
  const totalCpu = agentCpu + sidecarCpu;
  const minPodMemory = totalCpu * AUTOPILOT_RATIO_MIN;
  const maxPodMemory = totalCpu * AUTOPILOT_RATIO_MAX;
  const minAgentMemory = minPodMemory - sidecarMemoryGi;
  const maxAgentMemory = maxPodMemory - sidecarMemoryGi;
  const minMemoryGi = Math.max(1, Math.ceil(minAgentMemory));
  const maxMemoryGi = Math.floor(maxAgentMemory);
  return { minMemoryGi, maxMemoryGi };
}
function computePodRatio(agentCpu, agentMemoryGi, sidecarCpu, sidecarMemoryGi) {
  const totalCpu = agentCpu + sidecarCpu;
  const totalMemory = agentMemoryGi + sidecarMemoryGi;
  if (totalCpu === 0) return 0;
  return totalMemory / totalCpu;
}
function isValidAutopilotConfig(agentCpu, agentMemoryGi, sidecarCpu, sidecarMemoryGi) {
  const ratio = computePodRatio(agentCpu, agentMemoryGi, sidecarCpu, sidecarMemoryGi);
  return ratio >= AUTOPILOT_RATIO_MIN && ratio <= AUTOPILOT_RATIO_MAX;
}

// src/utils/service-definitions.ts
var POSTGRES_ENV = {
  POSTGRES_HOST_AUTH_METHOD: "trust",
  POSTGRES_DB: "conveyor"
};
var FIREBASE_EMULATOR_COMMAND = [
  "sh",
  "-c",
  `set -e; mkdir -p /home/node && cd /home/node && cat > firebase.json <<'EOF'
{"emulators":{"auth":{"host":"0.0.0.0","port":9099},"hub":{"host":"0.0.0.0","port":4400},"ui":{"enabled":false}}}
EOF
exec firebase emulators:start --only=auth --project=rally-cry-dev`
];
var CATALOG = {
  postgresql: {
    name: "postgresql",
    image: "postgres:16-alpine",
    command: [
      "sh",
      "-c",
      // Start postgres, wait for readiness, then create the test database.
      // Durability flags: service state is ephemeral by design (overlayfs, no
      // PVC; a crash re-seeds from pod-data), so commits must never wait on a
      // WAL flush. fsync=off + synchronous_commit=off + full_page_writes=off
      // remove all blocking storage I/O — without them, per-commit flushes on
      // the node's network boot disk dominated int-test wall time (~100ms/commit).
      "if [ ! -s /var/lib/postgresql/data/PG_VERSION ] && [ -d /var/lib/postgresql/pod-data ]; then cp -a /var/lib/postgresql/pod-data/. /var/lib/postgresql/data/; fi; chown -R postgres:postgres /var/lib/postgresql/data; docker-entrypoint.sh postgres -c fsync=off -c synchronous_commit=off -c full_page_writes=off & PID=$!; for i in $(seq 1 30); do pg_isready -U postgres && break; sleep 1; done; createdb -U postgres conveyor_test 2>/dev/null || true; wait $PID"
    ],
    ports: [5432],
    livenessProbe: {
      exec: ["pg_isready", "-U", "postgres"],
      periodSeconds: 30,
      failureThreshold: 3,
      timeoutSeconds: 5,
      initialDelaySeconds: 60
    },
    env: { ...POSTGRES_ENV },
    resources: {
      // postgres runs from the baked seed at /var/lib/postgresql/pod-data via
      // overlayfs CoW (no emptyDir — see k8s-pod-spec.ts), so WAL + catalog +
      // re-seed churn charges the container's ephemeral-storage. It must exceed
      // the ~1Gi ceiling where pods evicted, but is bounded by TWO Autopilot rules:
      //   1) limit == request — Autopilot caps the limit DOWN to the request at
      //      admission, so headroom must live on the REQUEST, not just the limit;
      //   2) the SUM of all container ephemeral requests in a pod must be ≤ 10Gi
      //      (an emptyDir doesn't escape this: its usage still evicts against the
      //      container limit, and raising the limit re-hits rule 1 → the cap).
      // The agent is trimmed to 4Gi (resource-tiers.ts) to free room: with
      // agent 4 + gcsfuse 1 + lgtm 1 + es/redis/firebase 0.75, postgres gets 3Gi
      // and the heaviest (URC) pod sits at 9.75Gi + 10Mi for GCS Fuse metadata
      // prefetch — under the 10Gi cap — while giving postgres 3x the ~1Gi
      // ceiling that evicted. Keep request == limit;
      // see the per-pod ephemeral budget guard test.
      // CPU: the request is the CFS floor; the old 50m starved postgres to 5ms
      // of CPU per 100ms period — every query burst hit throttle stalls, which
      // showed up as ~100ms floors on trivial statements and dominated the API
      // int suite even after the fsync flags above. 500m is paid for out of the
      // agent's derived share (resource-tiers.ts). The limit bursts to 2 for
      // spiky work — the first-boot pod-data seed copy and int-suite query
      // storms — borrowing idle node CPU without moving the request.
      requests: { cpuMillicores: 500, memoryMi: 512, ephemeralMi: 3 * 1024 },
      limits: { cpuMillicores: 2e3, memoryMi: 512, ephemeralMi: 3 * 1024 }
    },
    connectionEnv: {
      DATABASE_URL: "postgresql://postgres@postgresql:5432/conveyor",
      TEST_DATABASE_URL: "postgresql://postgres@postgresql:5432/conveyor_test"
    },
    statefulBake: true,
    bake: {
      // Start postgres, wait for readiness, then create the test database so
      // it's baked into the committed image. No pod-data restore (nothing is
      // seeded yet) and no durability flags (the bake's writes must land).
      // NOTE: docker-compose interpolates `$VAR`/`$(...)`, so every `$` that
      // must reach the shell is doubled.
      command: [
        "sh",
        "-c",
        "docker-entrypoint.sh postgres & PID=$$!; for i in $$(seq 1 30); do pg_isready -U postgres && break; sleep 1; done; createdb -U postgres conveyor_test 2>/dev/null || true; wait $$PID"
      ],
      environment: { ...POSTGRES_ENV },
      healthcheck: {
        test: ["CMD-SHELL", "pg_isready -U postgres"],
        intervalSec: 2,
        timeoutSec: 5,
        retries: 15
      }
    }
  },
  redis: {
    name: "redis",
    image: "redis:7-alpine",
    command: ["redis-server", "--appendonly", "yes", "--dir", "/data"],
    ports: [6379],
    env: {},
    resources: {
      requests: { cpuMillicores: 25, memoryMi: 32, ephemeralMi: 256 },
      limits: { cpuMillicores: 50, memoryMi: 64, ephemeralMi: 256 }
    },
    statefulBake: false,
    // Redis holds no baked state, so the bake runs the stock image CMD.
    bake: {},
    mirror: { src: "redis:7-alpine", dest: "mirror-redis:7-alpine" }
  },
  elasticsearch: {
    name: "elasticsearch",
    // 9.4.0 matches universal-rally-cry's local compose + its v9 ES client —
    // the v9 client sends Accept: compatible-with=9, which an 8.x server
    // rejects (media_type_header_exception), breaking search/audit indexing
    // in pods. 512m heap matches the project compose sizing.
    image: "docker.elastic.co/elasticsearch/elasticsearch:9.4.0",
    ports: [9200],
    // Baked service images are `docker commit`s of a recently-running ES, so
    // they carry a stale data-dir node.lock; ES 9 hard-fails on it at boot
    // ("Underlying file changed by an external force" → AlreadyClosedException).
    // Clear it before handing off to the stock entrypoint.
    command: [
      "sh",
      "-c",
      // ALL Lucene lock files, not just node.lock — the baked image also
      // carries per-index write.lock + snapshot_cache/write.lock, and ES 9
      // fail-fasts on any of them ("changed by an external force").
      "find /usr/share/elasticsearch/data -name '*.lock' -type f -delete 2>/dev/null; exec /usr/local/bin/docker-entrypoint.sh eswrapper"
    ],
    env: {
      "discovery.type": "single-node",
      "xpack.security.enabled": "false",
      "xpack.ml.enabled": "false",
      "xpack.watcher.enabled": "false",
      "xpack.profiling.enabled": "false",
      "ingest.geoip.downloader.enabled": "false",
      ES_JAVA_OPTS: "-Xms512m -Xmx512m"
    },
    resources: {
      // CPU limit 4x the request: ES cold-start is a CPU-bound JVM boot
      // (class loading + JIT + recovery of the docker-commit'ed data dir),
      // and the 500m hard cap put it at ~135s to yellow — past the sidecar
      // wait script's original 90s budget. Bursting to 2 cut it to ~40s on
      // the real cluster (A/B on identical nodes, 2 rounds). The burst only
      // borrows idle node CPU at boot; under contention CFS still floors ES
      // at its 500m request.
      requests: { cpuMillicores: 500, memoryMi: 1024, ephemeralMi: 256 },
      limits: { cpuMillicores: 2e3, memoryMi: 1024, ephemeralMi: 256 }
    },
    statefulBake: true,
    bake: {
      // No lock-clearing command at bake time: the bake starts from the stock
      // image, which has no committed data dir to unlock yet.
      environment: {
        "discovery.type": "single-node",
        "xpack.security.enabled": "false",
        ES_JAVA_OPTS: "-Xms512m -Xmx512m"
      }
    }
  },
  // All-in-one image bundling Grafana + Loki + Tempo + Mimir + an OTEL Collector.
  // Pinned tag (not :latest) so the image-builder content hash stays
  // deterministic across upstream releases — see image-builder.ts content-hash
  // dedup keyed on `deps.sorted()`.
  lgtm: {
    name: "lgtm",
    image: "grafana/otel-lgtm:0.11.6",
    // The otel-lgtm image's own CMD is ["/otel-lgtm/run-all.sh"] (WORKDIR
    // /otel-lgtm). Declaring it explicitly makes lgtm a command-based service so
    // the lazy start-file gate wraps it like the others — otherwise it launches
    // via image CMD at boot and can't be parked (which is why warm-booting it at
    // a minimal request OOMKilled it). getSidecarSpecs strips this command for a
    // baked lgtm image, so it only applies to the stock image whose launcher is
    // exactly this path.
    command: ["/otel-lgtm/run-all.sh"],
    ports: [
      // OTLP gRPC + HTTP — agents and user code emit telemetry here.
      4317,
      4318,
      // Grafana UI — reachable through the existing preview proxy on
      // https://3000-{sessionId}.preview.<PREVIEW_DOMAIN>/.
      3e3
    ],
    env: {
      // The pod is per-task; the preview proxy authenticates the session
      // upstream, so anonymous in-pod admin is acceptable here.
      GF_AUTH_ANONYMOUS_ENABLED: "true",
      GF_AUTH_ANONYMOUS_ORG_ROLE: "Admin",
      GF_SECURITY_ALLOW_EMBEDDING: "true",
      ENABLE_LOGS_GRAFANA: "true",
      ENABLE_LOGS_OTELCOL: "true"
    },
    resources: {
      // Autopilot caps the ephemeral-storage limit to the request (see the
      // postgresql note), so the 1Gi headroom must be on the request too.
      requests: { cpuMillicores: 250, memoryMi: 1024, ephemeralMi: 1024 },
      limits: { cpuMillicores: 1e3, memoryMi: 2048, ephemeralMi: 1024 }
    },
    statefulBake: false,
    // No `bake` block: a BAKE produces traces nobody reads, so starting the
    // (heavy) collector image would only add a pull + boot to every build.
    // lgtm still runs for live pods, and is never committed regardless.
    mirror: { src: "grafana/otel-lgtm:0.11.6", dest: "mirror-otel-lgtm:0.11.6" }
  },
  "firebase-auth-emulator": {
    name: "firebase-auth-emulator",
    image: "andreysenov/firebase-tools:latest",
    command: FIREBASE_EMULATOR_COMMAND,
    ports: [9099, 4400],
    env: {
      // The emulator container inherits the pod's Workload Identity, so
      // firebase-tools finds GCP credentials and its `emulators:start` does an
      // online "auto auth" + project validation that stalls ~47s on the pod's
      // locked-down egress (the dominant service boot cost — measured live).
      // The emulator needs NO real credentials, so cut off credential discovery
      // for this container only: google-auth-library skips metadata detection
      // and finds no key file, firebase-tools logs "not authenticated" and
      // starts the emulator immediately. The agent container keeps its WI.
      METADATA_SERVER_DETECTION: "none",
      GOOGLE_APPLICATION_CREDENTIALS: "/dev/null"
    },
    resources: {
      requests: { cpuMillicores: 100, memoryMi: 256, ephemeralMi: 256 },
      limits: { cpuMillicores: 500, memoryMi: 512, ephemeralMi: 256 }
    },
    connectionEnv: {
      // In docker-compose, services reach each other by service name — the
      // runtime pod equivalents use `localhost` because co-located services
      // share the pod's network namespace.
      FIREBASE_AUTH_EMULATOR_HOST: "firebase-auth-emulator:9099",
      NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST: "firebase-auth-emulator:9099"
    },
    statefulBake: false,
    mirror: {
      src: "andreysenov/firebase-tools:latest",
      dest: "mirror-firebase-tools:latest"
    },
    bake: {
      // The emulator writes its config at startup, so the bake needs the same
      // inline command the runtime uses.
      command: FIREBASE_EMULATOR_COMMAND,
      healthcheck: {
        test: ["CMD-SHELL", "wget -qO- http://localhost:9099/ >/dev/null 2>&1 || exit 1"],
        intervalSec: 3,
        timeoutSec: 5,
        retries: 20
      }
    }
  }
};
var SERVICE_DEFINITIONS = CATALOG;
var SUPPORTED_CLAUDESPACE_DEPS = Object.keys(CATALOG);
var STATEFUL_BAKE_DEPS = new Set(
  SUPPORTED_CLAUDESPACE_DEPS.filter((dep) => SERVICE_DEFINITIONS[dep].statefulBake)
);
var MIRRORABLE_SIDECARS = Object.fromEntries(
  SUPPORTED_CLAUDESPACE_DEPS.flatMap((dep) => {
    const mirror = SERVICE_DEFINITIONS[dep].mirror;
    return mirror ? [[dep, mirror]] : [];
  })
);
function sidecarRequestTotals(deps) {
  let totalCpu = 0;
  let totalMemoryMi = 0;
  for (const dep of deps) {
    const definition = SERVICE_DEFINITIONS[dep];
    if (!definition) continue;
    totalCpu += definition.resources.requests.cpuMillicores / 1e3;
    totalMemoryMi += definition.resources.requests.memoryMi;
  }
  return { cpu: totalCpu, memoryGi: totalMemoryMi / 1024 };
}

// src/utils/prestige.ts
var PRESTIGE_XP_MULTIPLIER = 1.1;
var MAX_PRESTIGE = 4;
var PRESTIGE_TIERS = [
  { prestige: 1, name: "Common", color: "#22c55e", iconPath: "/storypoints/square-solid-full.svg" },
  { prestige: 2, name: "Magic", color: "#3b82f6", iconPath: "/storypoints/diamond-solid-full.svg" },
  { prestige: 3, name: "Rare", color: "#eab308", iconPath: "/storypoints/gem-solid-full.svg" },
  {
    prestige: 4,
    name: "Unique",
    color: "#f97316",
    iconPath: "/storypoints/scroll-sharp-solid-full.svg"
  }
];
function scaleUp(baseXp, prestige) {
  if (prestige <= 0) return baseXp;
  return Math.ceil(baseXp * 11 ** prestige / 10 ** prestige);
}
function scaleDown(cycleXp, prestige) {
  if (prestige <= 0) return cycleXp;
  return Math.floor(cycleXp * 10 ** prestige / 11 ** prestige);
}
function prestigeCycleCost(prestige) {
  return scaleUp(xpToReachLevel(MAX_LEVEL), prestige);
}
function prestigeFromXp(totalXp) {
  const xp = Number.isFinite(totalXp) && totalXp > 0 ? Math.floor(totalXp) : 0;
  let prestige = 0;
  let cycleXp = xp;
  while (prestige < MAX_PRESTIGE && cycleXp >= prestigeCycleCost(prestige)) {
    cycleXp -= prestigeCycleCost(prestige);
    prestige++;
  }
  const { level } = levelFromXp(scaleDown(cycleXp, prestige));
  const levelFloor = scaleUp(xpToReachLevel(level), prestige);
  const atMaxLevel = level >= MAX_LEVEL;
  const xpForNextLevel = atMaxLevel ? 0 : scaleUp(xpToReachLevel(level + 1), prestige) - levelFloor;
  const xpIntoLevel = cycleXp - levelFloor;
  const progress = atMaxLevel ? 1 : Math.min(1, Math.max(0, xpForNextLevel === 0 ? 1 : xpIntoLevel / xpForNextLevel));
  return {
    prestige,
    tier: prestige > 0 ? PRESTIGE_TIERS[prestige - 1] ?? null : null,
    atMaxPrestige: prestige >= MAX_PRESTIGE,
    level,
    xpIntoLevel,
    xpForNextLevel,
    progress
  };
}

// src/utils/format-number.ts
var RAW_MAX = 9999.5;
var K_MAX = 999950;
var M_MAX = 99995e4;
function scaled(abs, divisor, suffix) {
  return `${(abs / divisor).toFixed(1).replace(/\.0$/, "")}${suffix}`;
}
function formatCompactNumber(value) {
  if (!Number.isFinite(value)) return "0";
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  if (abs < RAW_MAX) return `${sign}${Math.round(abs)}`;
  if (abs < K_MAX) return `${sign}${scaled(abs, 1e3, "K")}`;
  if (abs < M_MAX) return `${sign}${scaled(abs, 1e6, "M")}`;
  return `${sign}${scaled(abs, 1e9, "B")}`;
}
function formatCompactSigned(value) {
  const formatted = formatCompactNumber(value);
  return value > 0 ? `+${formatted}` : formatted;
}
function formatCompactUsd(value) {
  if (!Number.isFinite(value)) return "$0";
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  if (abs < RAW_MAX) return `${sign}$${abs.toFixed(2)}`;
  return `${sign}$${formatCompactNumber(abs)}`;
}

// src/utils/pod-size-presets.ts
var POD_SIZE_NAMES = ["small", "medium", "large"];
var POD_SIZE_PRESETS = {
  small: { cpu: 1.95, memory: 7 },
  medium: { cpu: 3.9, memory: 14 },
  large: { cpu: 5.25, memory: 18 }
};
var DEFAULT_POD_SIZE = "small";
function isPodSizeName(value) {
  return typeof value === "string" && POD_SIZE_NAMES.includes(value);
}

// src/utils/compute-provider.ts
var COMPUTE_PROVIDER_IDS = [
  "github",
  "claudespace",
  "none"
];
var DEFAULT_COMPUTE_PROVIDER = "github";
function resolveComputeProvider(value) {
  return COMPUTE_PROVIDER_IDS.includes(value) ? value : DEFAULT_COMPUTE_PROVIDER;
}
function isComputeDisabled(value) {
  return resolveComputeProvider(value) === "none";
}
var COMPUTE_DISABLED_REASON = 'Compute is set to "None" for this project, so cards do not spawn environments. Connect a local agent, or pick a compute provider in Settings \u2192 Cloud.';

// src/utils/chat-provider.ts
var CHAT_PROVIDER_IDS = ["slack", "discord", "none"];
var DEFAULT_CHAT_PROVIDER = "none";
function resolveChatProvider(value) {
  return CHAT_PROVIDER_IDS.includes(value) ? value : DEFAULT_CHAT_PROVIDER;
}
function chatProviderExclusiveReason(connected) {
  const name = connected === "slack" ? "Slack" : "Discord";
  return `A project can have one chat integration \u2014 disconnect ${name} in Settings \u2192 Integrations first.`;
}

// src/utils/json-merge-patch.ts
function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value) && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);
}
function applyJsonMergePatch(target, patch) {
  if (!isPlainObject(patch)) return patch;
  const base = isPlainObject(target) ? { ...target } : {};
  for (const [key, value] of Object.entries(patch)) {
    if (value === null) {
      delete base[key];
    } else if (isPlainObject(value)) {
      base[key] = applyJsonMergePatch(base[key], value);
    } else {
      base[key] = value;
    }
  }
  return base;
}

// src/utils/boot-progress.ts
var V3_BOOT_STEP_KEYS = [
  "pod_created",
  "pod_scheduled",
  "containers_ready",
  "workbench_ready",
  "repo_synced",
  "agent_connected",
  "sidecars_ready",
  "branch_ready",
  "agent_live",
  "start_command_launched",
  "app_serving"
];
var POD_REPORTABLE_BOOT_STEPS = [
  "workbench_ready",
  "repo_synced",
  "sidecars_ready",
  "branch_ready",
  "agent_live",
  "start_command_launched"
];
function isPodReportableBootStep(value) {
  return typeof value === "string" && POD_REPORTABLE_BOOT_STEPS.includes(value);
}
function isBootStepKey(value) {
  return typeof value === "string" && V3_BOOT_STEP_KEYS.includes(value);
}
var V3_BOOT_STEPS = [
  { key: "pod_created", label: "Pod created", track: "agent", fallbackWeightMs: 2e3 },
  { key: "pod_scheduled", label: "Pod scheduled", track: "agent", fallbackWeightMs: 4e3 },
  { key: "containers_ready", label: "Containers up", track: "agent", fallbackWeightMs: 14e3 },
  { key: "workbench_ready", label: "Workbench daemon up", track: "agent", fallbackWeightMs: 2e3 },
  { key: "repo_synced", label: "Repository synced", track: "agent", fallbackWeightMs: 6e3 },
  { key: "agent_connected", label: "Agent connected", track: "agent", fallbackWeightMs: 6e3 },
  { key: "sidecars_ready", label: "Sidecars ready", track: "agent", fallbackWeightMs: 1e3 },
  { key: "branch_ready", label: "Branch checked out", track: "agent", fallbackWeightMs: 2e3 },
  { key: "agent_live", label: "Agent working", track: "agent", fallbackWeightMs: 4e3 },
  {
    key: "start_command_launched",
    label: "App launched",
    track: "app",
    fallbackWeightMs: 2e3
  },
  { key: "app_serving", label: "App serving", track: "app", fallbackWeightMs: 12e4 }
];
var CODESPACE_BOOT_STEPS = [
  { key: "pod_created", label: "Codespace created", track: "agent", fallbackWeightMs: 4e3 },
  { key: "pod_scheduled", label: "VM starting", track: "agent", fallbackWeightMs: 2e4 },
  { key: "containers_ready", label: "VM up", track: "agent", fallbackWeightMs: 12e4 },
  { key: "agent_connected", label: "Agent connected", track: "agent", fallbackWeightMs: 9e4 },
  { key: "agent_live", label: "Agent working", track: "agent", fallbackWeightMs: 3e4 },
  {
    key: "start_command_launched",
    label: "App launched",
    track: "app",
    fallbackWeightMs: 2e3
  },
  { key: "app_serving", label: "App serving", track: "app", fallbackWeightMs: 12e4 }
];
function bootStepsForBackend(backend) {
  return backend === "codespace" ? CODESPACE_BOOT_STEPS : V3_BOOT_STEPS;
}
function sanitizeBootTimeline(raw) {
  if (!Array.isArray(raw)) return [];
  const byKey = /* @__PURE__ */ new Map();
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const key = entry.key;
    const at = entry.at;
    if (!isBootStepKey(key) || typeof at !== "string") continue;
    if (Number.isNaN(Date.parse(at))) continue;
    if (!byKey.has(key)) byKey.set(key, { key, at });
  }
  return V3_BOOT_STEP_KEYS.filter((k) => byKey.has(k)).map((k) => byKey.get(k));
}
function summarizeBootEstimates(samples) {
  const estimates = {};
  for (const sample of samples) {
    const at = /* @__PURE__ */ new Map();
    for (const m of sanitizeBootTimeline(sample)) at.set(m.key, Date.parse(m.at));
    for (let i = 1; i < V3_BOOT_STEP_KEYS.length; i++) {
      const key = V3_BOOT_STEP_KEYS[i];
      const prev = V3_BOOT_STEP_KEYS[i - 1];
      const cur = at.get(key);
      const before = at.get(prev);
      if (cur === void 0 || before === void 0) continue;
      const delta = cur - before;
      if (delta < 0) continue;
      const current = estimates[key];
      if (current === void 0 || delta > current) estimates[key] = delta;
    }
  }
  return { estimates, sampleCount: samples.length };
}
function weightFor(key, estimates, defs) {
  const est = estimates[key];
  if (typeof est === "number" && est >= 0) return est;
  return defs.find((s) => s.key === key)?.fallbackWeightMs ?? 1;
}
function buildSteps(defs, doneAt, now) {
  const steps = [];
  let activeKey = null;
  let prevAt = null;
  for (const def of defs) {
    const doneTs = doneAt.get(def.key);
    if (doneTs !== void 0) {
      const at = Date.parse(doneTs);
      const durationMs = prevAt === null ? void 0 : Math.max(0, at - prevAt);
      steps.push({
        key: def.key,
        label: def.label,
        track: def.track,
        status: "done",
        at: doneTs,
        durationMs
      });
      prevAt = at;
    } else if (activeKey === null) {
      activeKey = def.key;
      const durationMs = prevAt === null ? void 0 : Math.max(0, now - prevAt);
      steps.push({
        key: def.key,
        label: def.label,
        track: def.track,
        status: "active",
        durationMs
      });
    } else {
      steps.push({ key: def.key, label: def.label, track: def.track, status: "pending" });
    }
  }
  return { steps, activeKey };
}
function trackProgress(defs, allSteps, doneAt, estimates, now) {
  const keys = new Set(defs.map((d) => d.key));
  const steps = allSteps.filter((s) => keys.has(s.key));
  const totalWeight = defs.reduce((sum, s) => sum + weightFor(s.key, estimates, defs), 0) || 1;
  const firstUndoneIndex = steps.findIndex((s) => s.status === "active" || s.status === "pending");
  if (firstUndoneIndex === -1) return { fraction: 1, etaMs: null, complete: true, steps };
  let active = steps[firstUndoneIndex];
  if (active.status === "pending") {
    let lastDoneMs = null;
    for (const at of doneAt.values()) {
      const ts = Date.parse(at);
      if (lastDoneMs === null || ts > lastDoneMs) lastDoneMs = ts;
    }
    active = {
      ...active,
      status: "active",
      durationMs: lastDoneMs === null ? void 0 : Math.max(0, now - lastDoneMs)
    };
    steps[firstUndoneIndex] = active;
  }
  let doneWeight = 0;
  for (const def of defs) {
    if (doneAt.has(def.key)) doneWeight += weightFor(def.key, estimates, defs);
  }
  const activeWeight = weightFor(active.key, estimates, defs);
  const runningMs = active.durationMs ?? 0;
  const partial = activeWeight > 0 ? Math.min(runningMs / activeWeight, 0.95) * activeWeight : 0;
  const pending = defs.filter((d) => d.key !== active.key && !doneAt.has(d.key)).reduce((sum, d) => sum + weightFor(d.key, estimates, defs), 0);
  const fraction = Math.min((doneWeight + partial) / totalWeight, 0.99);
  const etaMs = Math.max(0, Math.round(activeWeight - runningMs) + pending);
  return { fraction, etaMs, complete: false, steps };
}
function computeBootProgress(input) {
  const estimates = input.estimates ?? {};
  const defs = input.steps ?? V3_BOOT_STEPS;
  const milestones = sanitizeBootTimeline(input.milestones);
  const doneAt = /* @__PURE__ */ new Map();
  for (const m of milestones) doneAt.set(m.key, m.at);
  const firstAt = milestones.length > 0 ? Date.parse(milestones[0].at) : null;
  const elapsedMs = firstAt === null ? null : Math.max(0, input.now - firstAt);
  const { steps: allSteps, activeKey } = buildSteps(defs, doneAt, input.now);
  const agentDefs = defs.filter((s) => s.track === "agent");
  const appDefs = defs.filter((s) => s.track === "app");
  const agent = trackProgress(agentDefs, allSteps, doneAt, estimates, input.now);
  const app = trackProgress(appDefs, allSteps, doneAt, estimates, input.now);
  return {
    fraction: agent.fraction,
    steps: agent.steps,
    activeKey,
    complete: agent.complete,
    elapsedMs,
    etaMs: agent.etaMs,
    app
  };
}

// src/utils/task-planning.ts
var PRE_BUILD_TASK_STATUSES = /* @__PURE__ */ new Set(["Planning", "Open"]);
function hasTaskPlan(plan) {
  return !!plan?.trim();
}

// src/utils/task-card-dto.ts
function toCardFromTaskDTO(dto) {
  return {
    id: dto.id,
    slug: dto.slug,
    subProjectId: dto.subProjectId,
    title: dto.title,
    status: dto.status,
    statusChangedAt: dto.statusChangedAt,
    isRelease: dto.isRelease,
    releaseTaskId: dto.releaseTaskId,
    releaseTitle: dto.releaseTitle,
    hasPlan: !!dto.plan?.trim(),
    priorityId: dto.priorityId,
    priority: dto.priority,
    riskId: dto.riskId,
    risk: dto.risk,
    taskTags: dto.taskTags,
    onHold: dto.onHold,
    iconId: dto.iconId,
    parentIconId: dto.parentIconId,
    storyPointId: dto.storyPointId,
    storyPoint: dto.storyPoint,
    description: dto.description,
    agentId: dto.agentId,
    agent: dto.agent ?? null,
    prAdditions: dto.prAdditions,
    prDeletions: dto.prDeletions,
    prLintWarnings: dto.prLintWarnings,
    prMergedAt: dto.prMergedAt,
    fatigued: dto.fatigued,
    assignedUser: dto.assignedUser,
    reviewers: dto.reviewers,
    queuedByUserId: dto.queuedByUserId,
    queuedByUserName: dto.queuedByUserName,
    queuedUntil: dto.queuedUntil,
    branchLabel: dto.branchLabel,
    githubPRNumber: dto.githubPRNumber,
    githubPRUrl: dto.githubPRUrl,
    codespaceId: dto.codespaceId,
    codespaceUrl: dto.codespaceUrl,
    codespaceHostUrl: dto.codespaceHostUrl,
    codespaceStatus: dto.codespaceStatus,
    codespaceProvider: dto.codespaceProvider,
    identifying: dto.identifying,
    type: dto.type,
    source: dto.source,
    score: dto.score,
    fingerprint: dto.fingerprint,
    mergeQueuedByUserId: dto.mergeQueuedByUserId,
    codeReviewStatus: dto.codeReviewStatus,
    aiReviewedAt: dto.aiReviewedAt,
    humanReviewedAt: dto.humanReviewedAt,
    agentRunnerStatus: dto.agentRunnerStatus,
    lastHeartbeatAt: dto.lastHeartbeatAt,
    hasActivePod: dto.hasActivePod,
    activeSubscriptionKeyLabel: dto.activeSubscriptionKeyLabel,
    parentTaskId: dto.parentTaskId,
    ordinal: dto.ordinal,
    featureBranch: dto.featureBranch,
    isDuplicate: dto.isDuplicate,
    childTaskCount: dto.childTaskCount,
    childTaskDoneCount: dto.childTaskDoneCount,
    childStatusCounts: dto.childStatusCounts,
    aggregatedAdditions: dto.aggregatedAdditions,
    aggregatedDeletions: dto.aggregatedDeletions,
    releaseRiskSummary: dto.releaseRiskSummary,
    releaseStoryPointSummary: dto.releaseStoryPointSummary,
    chatId: dto.chatId,
    hasUnmetDependencies: dto.hasUnmetDependencies,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt
  };
}

// src/utils/code-location.ts
var DEV_ENVIRONMENT = "dev";
var PROD_ENVIRONMENT = "prod";
var CODE_LOCATION_PHASES = [
  /** Merged to the dev branch, but no dev deploy has picked it up yet. */
  "pending-dev",
  /** A dev deploy carrying this card is in flight. */
  "deploying-dev",
  /** A dev deploy that includes this card has completed successfully. */
  "in-dev",
  /** A prod deploy carrying this card's release is in flight. */
  "deploying-prod",
  /** The card's code is live in production. */
  "in-prod"
];
var PROMOTED_STATUSES = /* @__PURE__ */ new Set(["ReviewLive", "Complete"]);
var MERGED_STATUSES = /* @__PURE__ */ new Set(["ReviewDev", "ReviewLive", "Complete"]);
function toMs(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}
function findReleaseRun(task, snapshot) {
  const releaseTaskId = task.isRelease ? task.id : task.releaseTaskId ?? null;
  if (!releaseTaskId) return null;
  return snapshot.releaseRuns.find((run) => run.releaseTaskId === releaseTaskId) ?? null;
}
function prodLocation(run) {
  const environment = run.environment || PROD_ENVIRONMENT;
  if (run.status !== "completed") {
    return { phase: "deploying-prod", environment, deploying: true };
  }
  if (run.conclusion !== "success") {
    return { phase: "deploying-prod", environment, deploying: true };
  }
  return { phase: "in-prod", environment, deploying: false };
}
function devLocation(mergedAtMs, snapshot) {
  const dev = snapshot.environments.find((env) => env.environment === DEV_ENVIRONMENT);
  const pending = {
    phase: "pending-dev",
    environment: DEV_ENVIRONMENT,
    deploying: false
  };
  if (!dev) return pending;
  const lastSuccessAt = toMs(dev.lastSuccess?.completedAt);
  if (lastSuccessAt !== null && lastSuccessAt >= mergedAtMs) {
    return { phase: "in-dev", environment: DEV_ENVIRONMENT, deploying: false };
  }
  const activeStartedAt = toMs(dev.active?.startedAt);
  if (dev.active && (activeStartedAt === null || activeStartedAt >= mergedAtMs)) {
    return { phase: "deploying-dev", environment: DEV_ENVIRONMENT, deploying: true };
  }
  return pending;
}
function resolveCodeLocation(task, snapshot) {
  if (!snapshot) return null;
  const promoted = PROMOTED_STATUSES.has(task.status);
  const merged = MERGED_STATUSES.has(task.status) && Boolean(task.prMergedAt);
  if (!promoted && !merged) return null;
  const releaseRun = findReleaseRun(task, snapshot);
  if (releaseRun) return prodLocation(releaseRun);
  if (promoted) {
    return { phase: "in-prod", environment: PROD_ENVIRONMENT, deploying: false };
  }
  const mergedAtMs = toMs(task.prMergedAt);
  if (mergedAtMs === null) return null;
  return devLocation(mergedAtMs, snapshot);
}

// src/utils/tag-path-match.ts
function parseTagContextPaths(contextPaths) {
  if (!Array.isArray(contextPaths)) return [];
  const refs = [];
  for (const entry of contextPaths) {
    if (typeof entry !== "object" || entry === null) continue;
    const { path, type } = entry;
    if (typeof path !== "string" || path.length === 0) continue;
    if (type !== "rule" && type !== "doc" && type !== "file" && type !== "folder") continue;
    refs.push({ path: normalizePath(path), type });
  }
  return refs;
}
function normalizePath(path) {
  return path.replace(/^\.\//, "").replace(/\/+$/, "");
}
function refMatchesPath(ref, path) {
  if (path === ref.path) return true;
  return ref.type === "folder" && path.startsWith(`${ref.path}/`);
}
function matchTagsByPaths(paths, tags) {
  const uniquePaths = [...new Set(paths.map(normalizePath).filter(Boolean))];
  if (uniquePaths.length === 0) return [];
  const matches = [];
  for (const tag of tags) {
    const refs = parseTagContextPaths(tag.contextPaths);
    if (refs.length === 0) continue;
    const matched = uniquePaths.filter((path) => refs.some((ref) => refMatchesPath(ref, path)));
    if (matched.length > 0) {
      matches.push({ tagId: tag.id, tagName: tag.name, matchedPaths: matched });
    }
  }
  return matches.sort(
    (a, b) => b.matchedPaths.length - a.matchedPaths.length || a.tagName.localeCompare(b.tagName)
  );
}
var REPO_PATH_PATTERN = /(?:^|[\s`("'[])((?:apps|packages|scripts|docs|\.claude|\.github)\/[A-Za-z0-9_@][A-Za-z0-9_@\-./]*)/g;
function extractRepoPaths(markdown) {
  const found = /* @__PURE__ */ new Set();
  for (const match of markdown.matchAll(REPO_PATH_PATTERN)) {
    const cleaned = match[1].replace(/[).,:;!?]+$/, "").replace(/\/+$/, "");
    if (cleaned.includes("/")) found.add(cleaned);
  }
  return [...found];
}

// src/utils/tag-snapshot-diff.ts
function contextPathKey(link) {
  return [link.type, link.path, link.label ?? ""].join("|");
}
function contextPathsEqual(a, b) {
  const left = (a ?? []).map(contextPathKey).sort();
  const right = (b ?? []).map(contextPathKey).sort();
  return left.length === right.length && left.every((value, i) => value === right[i]);
}
function idsEqual(a, b) {
  const left = [...a ?? []].sort();
  const right = [...b ?? []].sort();
  return left.length === right.length && left.every((value, i) => value === right[i]);
}
function tagSnapshotsEqual(before, after) {
  return before.name === after.name && before.color === after.color && (before.description ?? "") === (after.description ?? "") && (before.overview ?? "") === (after.overview ?? "") && (before.overviewPath ?? "") === (after.overviewPath ?? "") && before.ordinal === after.ordinal && contextPathsEqual(before.contextPaths, after.contextPaths) && idsEqual(before.parentTagIds, after.parentTagIds);
}
function renderContextLink(link) {
  const label = link.label ? [" (", link.label, ")"].join("") : "";
  return ["  ", link.type, ": ", link.path, label].join("");
}
function renderContextPaths(links) {
  if (!links || links.length === 0) return "  (none)";
  return links.map(renderContextLink).sort().join("\n");
}
function renderParents(ids, resolveName) {
  if (!ids || ids.length === 0) return "  (none)";
  return ids.map((id) => ["  ", resolveName?.(id) ?? id].join("")).sort().join("\n");
}
function tagSnapshotToText(snapshot, resolveName) {
  if (!snapshot) return "";
  return [
    ["name: ", snapshot.name].join(""),
    ["color: ", snapshot.color].join(""),
    ["order: ", String(snapshot.ordinal)].join(""),
    ["description: ", snapshot.description ?? ""].join(""),
    "parents:",
    renderParents(snapshot.parentTagIds, resolveName),
    "context links:",
    renderContextPaths(snapshot.contextPaths),
    ["overview path: ", snapshot.overviewPath ?? ""].join(""),
    "overview:",
    snapshot.overview ?? "",
    ""
  ].join("\n");
}

// src/utils/context-path-warnings.ts
function buildRepoEntryTypeMap(entries) {
  const map = /* @__PURE__ */ new Map();
  for (const entry of entries) map.set(entry.path, entry.type);
  return map;
}
function findContextPathWarning(link, entryTypes, contentByPath) {
  const path = normalizeContextPath(link.path);
  if (!path) return null;
  if (CONTEXT_PATH_UNTRACKED_PREFIXES.some((prefix) => path.startsWith(prefix))) return null;
  const wantsDirectory = link.type === "folder";
  const entryType = entryTypes.get(path);
  if (!entryType) return "notFound";
  if (wantsDirectory && entryType !== "tree") return "expectedFolder";
  if (!wantsDirectory && entryType === "tree") return "expectedFile";
  const { locator, locatorType } = link;
  if (!locator || !locatorType || isPlaceholderLocator(locator)) return null;
  const content = contentByPath?.get(path) ?? null;
  if (content === null) return null;
  if (!locatorMatchesContent(content, locatorType, locator)) return "locatorNotFound";
  return null;
}
function findContextPathWarnings(links, entryTypes, contentByPath) {
  const warnings = [];
  for (const link of links) {
    const warning = findContextPathWarning(link, entryTypes, contentByPath);
    if (warning) {
      warnings.push({
        path: link.path,
        type: link.type,
        warning,
        ...warning === "locatorNotFound" && link.locator ? { locator: link.locator } : {}
      });
    }
  }
  return warnings;
}

// src/utils/tag-overview-source.ts
function overviewSourceLink(overviewPath) {
  return { type: "doc", path: overviewPath };
}
function toIso(value) {
  if (value === null || value === void 0) return void 0;
  return typeof value === "string" ? value : value.toISOString();
}
function resolveTagOverview(row, checks) {
  if (!row.overviewPath) return { overview: row.overview ?? null, source: null };
  const sha = row.overviewFileSha ?? void 0;
  const syncedAt = toIso(row.overviewFileSyncedAt);
  if (row.overviewFileCache === null || row.overviewFileCache === void 0) {
    return {
      overview: row.overview ?? null,
      source: { state: "pending", ...sha === void 0 ? {} : { sha } }
    };
  }
  const verification = verificationForLink(overviewSourceLink(row.overviewPath), checks ?? null);
  return {
    overview: row.overviewFileCache,
    source: {
      state: verification.status === "stale" ? "stale" : "ok",
      ...sha === void 0 ? {} : { sha },
      ...syncedAt === void 0 ? {} : { syncedAt }
    }
  };
}
function hasEffectiveOverview(row, checks) {
  return (resolveTagOverview(row, checks).overview ?? "").trim().length > 0;
}

// src/utils/gcp-exceptional.ts
var EXCEPTIONAL_GCP_LOG_FILTER = "(severity>=WARNING OR httpRequest.status>=400)";
function isExceptionalLogEntry(entry) {
  return ["WARNING", "ERROR", "CRITICAL", "ALERT", "EMERGENCY"].includes(entry.severity) || (entry.httpRequest?.status ?? 0) >= 400;
}
function exceptionalLogKind(entry) {
  return ["ERROR", "CRITICAL", "ALERT", "EMERGENCY"].includes(entry.severity) || (entry.httpRequest?.status ?? 0) >= 500 ? "error" : "warning";
}
function normalize(value) {
  return value.replace(/(?<=\btrace=)[0-9a-f]+/gi, ":id").replace(/[0-9a-f]{8,}/gi, ":id").replace(/\b\d+\b/g, ":n").replace(/\/\d+(?=\/|$)/g, "/:id").trim().toLowerCase();
}
function exceptionalLogSource(entry) {
  return entry.resource.service_name ?? entry.resource.pod_name ?? entry.resource.database_id ?? entry.resourceType ?? "unknown";
}
function normalizedSearchTerms(entry) {
  return normalize(entry.httpRequest?.url ?? entry.message).replace(/:[a-z]+/g, " ").replace(/[^a-z0-9/_-]+/g, " ").trim().replace(/\s+/g, " ");
}
function representativeFilter(entry) {
  const http = entry.httpRequest;
  const stableTerms = normalizedSearchTerms(entry).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  const searchFilter = stableTerms ? `SEARCH("${stableTerms}")` : void 0;
  if (http && (http.status ?? 0) >= 400) {
    const method = http.method?.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    return [
      `httpRequest.status=${http.status}`,
      method ? `httpRequest.requestMethod="${method}"` : void 0,
      searchFilter
    ].filter(Boolean).join(" AND ");
  }
  return [`severity=${entry.severity}`, searchFilter].filter(Boolean).join(" AND ");
}
var SUMMARY_MAX_CHARS = 200;
function exceptionalLogSummary(entry) {
  const message = entry.message.trim();
  if (message) return message;
  const http = entry.httpRequest;
  if (http && (http.method || http.url || http.status)) {
    const target = [http.method, http.url].filter(Boolean).join(" ");
    const httpLine = http.status ? [target, http.status].filter(Boolean).join(" \u2192 ") : target;
    if (httpLine) return httpLine.slice(0, SUMMARY_MAX_CHARS);
  }
  const payload = entry.payload?.replace(/\s+/g, " ").trim();
  if (payload) return payload.slice(0, SUMMARY_MAX_CHARS);
  return `${entry.severity} log entry from ${exceptionalLogSource(entry)}`;
}
function exceptionalLogSignature(entry) {
  const source = exceptionalLogSource(entry);
  const http = entry.httpRequest;
  return [
    source,
    exceptionalLogKind(entry),
    http?.method ?? "",
    http?.status ?? "",
    normalize(http?.url ?? ""),
    normalize(entry.message)
  ].join("|");
}
function groupExceptionalLogEntries(entries, options) {
  const grouped = /* @__PURE__ */ new Map();
  let errors = 0;
  let warnings = 0;
  for (const entry of entries) {
    if (!isExceptionalLogEntry(entry)) continue;
    const kind = exceptionalLogKind(entry);
    if (kind === "error") errors += 1;
    else warnings += 1;
    const signature = exceptionalLogSignature(entry);
    const existing = grouped.get(signature);
    if (existing) {
      existing.count += 1;
      if (entry.timestamp < existing.firstSeen) existing.firstSeen = entry.timestamp;
      if (entry.timestamp > existing.lastSeen) existing.lastSeen = entry.timestamp;
      existing.samples.push(entry);
      continue;
    }
    grouped.set(signature, {
      id: signature,
      env: options.env,
      source: exceptionalLogSource(entry),
      kind,
      count: 1,
      firstSeen: entry.timestamp,
      lastSeen: entry.timestamp,
      summary: exceptionalLogSummary(entry),
      representativeFilter: representativeFilter(entry),
      samples: [entry]
    });
  }
  const groups = [...grouped.values()];
  for (const group of groups) {
    group.samples.sort((left, right) => right.timestamp.localeCompare(left.timestamp));
    group.samples = group.samples.slice(0, options.sampleLimit);
  }
  groups.sort(
    (left, right) => (left.kind === right.kind ? 0 : left.kind === "error" ? -1 : 1) || right.lastSeen.localeCompare(left.lastSeen) || right.count - left.count
  );
  return { errors, warnings, groups };
}

// src/utils/grafana-links.ts
function buildGrafanaExploreUrl(params) {
  const base = params.baseUrl.replace(/\/+$/, "");
  const panes = {
    a: {
      datasource: params.datasourceUid,
      queries: [
        {
          refId: "A",
          expr: params.logql,
          datasource: { type: "loki", uid: params.datasourceUid }
        }
      ],
      range: { from: String(params.fromMs), to: String(params.toMs) }
    }
  };
  return `${base}/explore?schemaVersion=1&panes=${encodeURIComponent(JSON.stringify(panes))}&orgId=1`;
}

// src/utils/slack-links.ts
function buildSlackThreadUrl({ channelId, messageTs, teamId }) {
  if (!channelId || !messageTs) return null;
  const ts = messageTs.replace(".", "");
  const base = `https://slack.com/archives/${channelId}/p${ts}`;
  return teamId ? `${base}?team=${teamId}` : base;
}

// src/utils/discord-links.ts
function buildDiscordThreadUrl({ guildId, threadId }) {
  if (!guildId || !threadId) return null;
  return `https://discord.com/channels/${guildId}/${threadId}`;
}
export {
  ACCESS_LEVEL_RANK,
  ACTIVE_WORK_STATUSES,
  ACTIVITY_KIND_FILTERS,
  ADMIN_CREATE_SERVICES,
  AGENT_CHAT_HISTORY_FETCH_LIMIT,
  AGENT_KEY_KINDS,
  AGENT_MILESTONE_SLUGS,
  AGENT_PROVIDERS,
  AGENT_STATUS_REASON_USER_QUESTION,
  ALLOWED_FILE_MIME_TYPES,
  ANTHROPIC_CATALOG,
  AUTOPILOT_RATIO_MAX,
  AUTOPILOT_RATIO_MIN,
  AddDependencyRequestSchema,
  AddProjectTaskDependencyRequestSchema,
  AddProjectTaskReviewerRequestSchema,
  AddTasksToProjectReleaseRequestSchema,
  AgentEventSchema,
  AgentHeartbeatSchema,
  AgentQuestionOptionSchema,
  AgentQuestionSchema,
  AnswerAgentQuestionRequestSchema,
  ApproveAndMergePRRequestSchema,
  ApproveManualTestRequestSchema,
  ApproveProjectManualTestRequestSchema,
  ApproveProjectMergePRRequestSchema,
  AskUserQuestionRequestSchema,
  BUILDER_STREAM_ID,
  CARD_DESCRIPTION_FIELD_HINT,
  CARD_DESCRIPTION_LIMIT_MESSAGE,
  CARD_DESCRIPTION_MAX,
  CARD_TYPE_STATUSES,
  CARD_TYPE_VALUES,
  CHAT_PROVIDER_IDS,
  CODESPACE_BOOT_STEPS,
  CODESPACE_SESSION_ACTIVE_RUNNER_STATUSES,
  CODE_LOCATION_PHASES,
  COMPUTE_DISABLED_REASON,
  COMPUTE_PROVIDER_IDS,
  CONTEXT_LINK_LOCATOR_MAX,
  CONTEXT_PATH_UNTRACKED_PREFIXES,
  CREATED_DESC_STATUSES,
  CRITICAL_AUTOMATED_SOURCES,
  CancelTaskQueuedMessageRequestSchema,
  ClearAgentTodosRequestSchema,
  ConfirmFileUploadRequestSchema,
  ConfirmProjectFileUploadRequestSchema,
  ConnectAgentRequestSchema,
  CreateFollowUpTaskRequestSchema,
  CreatePRInputSchema,
  CreatePRResponseSchema,
  CreateProjectPullRequestRequestSchema,
  CreateProjectReleaseRequestSchema,
  CreateProjectSubtaskRequestSchema,
  CreateProjectSuggestionRequestSchema,
  CreateProjectTagRequestSchema,
  CreateProjectTaskRequestSchema,
  CreatePullRequestRequestSchema,
  CreateSubtaskRequestSchema,
  CreateSuggestionRequestSchema,
  CycleCodingAgentKeyRequestSchema,
  DEFAULT_CHAT_PROVIDER,
  DEFAULT_COMPUTE_PROVIDER,
  DEFAULT_HAIKU_MODEL,
  DEFAULT_MODEL_PROVIDER,
  DEFAULT_OPUS_MODEL,
  DEFAULT_POD_SIZE,
  DEFAULT_RISK_LEVELS,
  DEFAULT_SONNET_MODEL,
  DEFAULT_TASK_STATUS_COLOR,
  DEPLOYMENT_TARGET_ENVIRONMENTS,
  DEPLOYMENT_TARGET_PROVIDERS,
  DEV_ENVIRONMENT,
  DeleteProjectSubtaskRequestSchema,
  DeleteSubtaskRequestSchema,
  DeleteSubtaskResponseSchema,
  DeleteTaskAuditRequestSchema,
  DiscoveredPortSchema,
  EMBEDDABLE_IMAGE_TYPES,
  EXCEPTIONAL_GCP_LOG_FILTER,
  EXTERNAL_AGENT_MESSAGE_SOURCE,
  EditManualTestRequestSchema,
  EditProjectManualTestRequestSchema,
  EmitAgentEventRequestSchema,
  EndReviewSessionRequestSchema,
  FABLE_MODEL,
  FlushSingleQueuedMessageRequestSchema,
  FlushTaskQueueRequestSchema,
  GCP_ENVS,
  GRAFANA_ENVS,
  GRAFANA_LOG_LEVELS,
  GenerateTaskIconRequestSchema,
  GetActiveAuditSessionsRequestSchema,
  GetActivePtySessionRequestSchema,
  GetAgentStatusRequestSchema,
  GetChatMessagesRequestSchema,
  GetCliHistoryRequestSchema,
  GetConnectionContextRequestSchema,
  GetDependenciesRequestSchema,
  GetProjectAttachmentRequestSchema,
  GetProjectAvailableTuisRequestSchema,
  GetProjectConnectUrlsRequestSchema,
  GetProjectOnboardingStatusRequestSchema,
  GetProjectOnboardingStepRequestSchema,
  GetProjectSummaryRequestSchema,
  GetProjectTagRequestSchema,
  GetProjectTaskChatRequestSchema,
  GetProjectTaskCliRequestSchema,
  GetProjectTaskDependenciesRequestSchema,
  GetProjectTaskRequestSchema,
  GetProjectTaskSessionsRequestSchema,
  GetPtyStreamEndpointRequestSchema,
  GetSuggestionsRequestSchema,
  GetTaskAuditAggregatesRequestSchema,
  GetTaskAuditRequestSchema,
  GetTaskAuditsRequestSchema,
  GetTaskContextRequestSchema,
  GetTaskFileRequestSchema,
  GetTaskFilesRequestSchema,
  GetTaskPropertiesRequestSchema,
  GetTaskRequestSchema,
  GetUiCliHistoryRequestSchema,
  HIDDEN_REPORT_STATUSES,
  HUMAN_PROSE_WRITING_STYLE,
  HandoffToImplementerRequestSchema,
  HeartbeatRequestSchema,
  HeartbeatResponseSchema,
  IDENTIFIED_WORK_STATUSES,
  IDLE_HEARTBEAT_MS,
  KnownAgentEventSchema,
  ListAccessibleProjectsRequestSchema,
  ListAccessibleSubprojectsRequestSchema,
  ListActivePtySessionsRequestSchema,
  ListIconsRequestSchema,
  ListKeysToProbeRequestSchema,
  ListManualTestsRequestSchema,
  ListMyLiveSessionsAcrossProjectsRequestSchema,
  ListMyLiveSessionsRequestSchema,
  ListProjectManualTestsRequestSchema,
  ListProjectMembersRequestSchema,
  ListProjectSessionGroupsRequestSchema,
  ListProjectSubtasksRequestSchema,
  ListProjectTagsRequestSchema,
  ListProjectTaskFilesRequestSchema,
  ListProjectTasksRequestSchema,
  ListSubtasksRequestSchema,
  MAX_AGENT_FLAVOR_LENGTH,
  MAX_DISCOVERED_PORTS,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_TAGS,
  MAX_FILE_TAG_LENGTH,
  MAX_LEVEL,
  MAX_PRESTIGE,
  MCP_TOOL_REGISTRY,
  MENTION_TOKEN_REGEX,
  MESSAGE_KINDS,
  MIN_CONTRIBUTOR_LEVEL,
  MIRRORABLE_SIDECARS,
  MODEL_PROVIDERS,
  MODEL_WIRE_FORMATS,
  MarkInitialPromptSubmittedRequestSchema,
  MoveProjectCardRequestSchema,
  NotifyAgentVersionRequestSchema,
  OPS_ENVIRONMENTS,
  OPS_TAB_REGISTRY,
  PM_CHAT_HISTORY_LIMIT,
  POD_REPORTABLE_BOOT_STEPS,
  POD_SIZE_NAMES,
  POD_SIZE_PRESETS,
  PRESTIGE_TIERS,
  PRESTIGE_XP_MULTIPLIER,
  PREVIEW_PORT_DENY_LIST,
  PREVIOUS_OPUS_MODEL,
  PREVIOUS_SONNET_MODEL,
  PRE_BUILD_TASK_STATUSES,
  PROD_ENVIRONMENT,
  PROJECT_FUNCTION_DEFAULTS,
  PROJECT_FUNCTION_IDS,
  PROJECT_TIMEZONE,
  PR_PRODUCING_AGENT_MODES,
  PTY_STREAM_PORT_ATTEMPTS,
  PTY_STREAM_PORT_BASE,
  PickFaIconRequestSchema,
  PostAgentMessageRequestSchema,
  PostChildChatMessageRequestSchema,
  PostToChatInputSchema,
  PostToChatRequestSchema,
  PostToChatResponseSchema,
  PostToProjectChatRequestSchema,
  PostToProjectTaskChatRequestSchema,
  ProjectTagContextPathSchema,
  PtyAttachRequestSchema,
  PtyChatAttachRequestSchema,
  PtyChatEventPayloadSchema,
  PtyChatEventRequestSchema,
  PtyEndedRequestSchema,
  PtyInputRequestSchema,
  PtyOutputRequestSchema,
  PtyResizeRequestSchema,
  PtyStreamFrameReader,
  PublishReviewGuideRequestSchema,
  QUEUE_JOBS_PAGE_SIZE,
  QUEUE_JOB_STATES,
  QueryManualTestsRequestSchema,
  QueryProjectGcpLogsRequestSchema,
  QueryProjectGrafanaLogsRequestSchema,
  QueryProjectManualTestsRequestSchema,
  REPORT_STATUS_TO_LANE,
  REPORT_XP_RATE,
  REPOSITORY_PROVIDER_IDS,
  REVIEWER_STREAM_ID,
  REVIEW_XP_RATE,
  RISK_LEVELS,
  RISK_RANK,
  RefreshCodingAgentKeyUsageRequestSchema,
  RefreshGithubTokenRequestSchema,
  RefreshGithubTokenResponseSchema,
  RejectManualTestRequestSchema,
  RejectProjectManualTestRequestSchema,
  RemoveDependencyRequestSchema,
  RemoveManualTestRequestSchema,
  RemoveProjectManualTestRequestSchema,
  RemoveProjectTaskDependencyRequestSchema,
  RemoveProjectTaskReviewerRequestSchema,
  ReportAgentStatusRequestSchema,
  ReportBootMilestoneRequestSchema,
  ReportDiscoveredPortsRequestSchema,
  ReportPtyStreamRequestSchema,
  ReportReviewSpawnFailureRequestSchema,
  ReportSessionSpawnFailureRequestSchema,
  ReportTaskAuditResultRequestSchema,
  RequestFileUploadRequestSchema,
  RequestProjectFileUploadRequestSchema,
  RequestWorkspaceRecycleRequestSchema,
  ResumeAdhocSessionRequestSchema,
  ReviewGuideContentSchema,
  ReviewGuideFileReferenceSchema,
  ReviewGuideSectionSchema,
  SERVICE_DEFINITIONS,
  SP_XP_RATE,
  STATEFUL_BAKE_DEPS,
  STRANDED_RUNNER_STATUSES,
  SUPPORTED_CLAUDESPACE_DEPS,
  SUPPORTED_WIRE_FORMATS,
  SearchFaIconsRequestSchema,
  SearchProjectTasksRequestSchema,
  SendSoftStopRequestSchema,
  SessionStartRequestSchema,
  SessionStartResponseSchema,
  SessionStopRequestSchema,
  SessionStopResponseSchema,
  SetManualTestsRequestSchema,
  SetProjectManualTestsRequestSchema,
  SpawnTaskSessionRequestSchema,
  StartAdhocSessionRequestSchema,
  StartChildCloudBuildRequestSchema,
  StartCodeReviewRequestSchema,
  StartProjectBuildRequestSchema,
  StartProjectWorkspaceRequestSchema,
  StartTagAuditRequestSchema,
  StartTaskAuditRequestSchema,
  StopAdhocSessionRequestSchema,
  StopChildBuildRequestSchema,
  StopCodeReviewRequestSchema,
  StopProjectBuildRequestSchema,
  StopProjectWorkspaceRequestSchema,
  StopTaskSessionRequestSchema,
  StoreSessionIdRequestSchema,
  StoreSessionIdResponseSchema,
  SubmitCodeReviewResultRequestSchema,
  TAG_DESCRIPTION_MAX,
  TAG_OVERVIEW_MAX,
  TAG_REASON_MAX,
  TASK_CHAT_HISTORY_LIMIT,
  TASK_LESS_WORKSPACE_PURPOSES,
  TASK_STATUS_COLORS,
  TASK_STATUS_ORDER,
  TERMINAL_TASK_STATUSES,
  TRIAGE_INCLUDE_VALUES,
  TUI_KINDS,
  TransitionProjectTaskStatusRequestSchema,
  TriggerIdentificationRequestSchema,
  UpdateChildStatusRequestSchema,
  UpdateProjectSubtaskRequestSchema,
  UpdateProjectTagRequestSchema,
  UpdateProjectTaskRequestSchema,
  UpdateSubtaskRequestSchema,
  UpdateTaskFieldsRequestSchema,
  UpdateTaskPropertiesRequestSchema,
  UpdateTaskStatusRequestSchema,
  UpdateTaskStatusResponseSchema,
  V3_BOOT_STEPS,
  V3_BOOT_STEP_KEYS,
  VerifyConnectionRequestSchema,
  VoteProjectSuggestionRequestSchema,
  VoteSuggestionRequestSchema,
  WORKSPACE_BACKENDS,
  WORKSPACE_DESIRED_STATES,
  WORKSPACE_OBSERVED_STATES,
  WORKSPACE_POD_PHASES,
  WORKSPACE_PURPOSES,
  WORKSPACE_SESSION_MODES,
  WORKSPACE_SESSION_ROLES,
  WORKSPACE_SESSION_STATUSES,
  WORKSPACE_TUNNEL_HEARTBEAT_MS,
  accessLevelRank,
  actionsPrebakeRegistrySchema,
  adminSupportsCreate,
  applyAgentFlavor,
  applyJsonMergePatch,
  availableTabsForEnvDTO,
  availableTabsFromConfigDTO,
  bootStepsForBackend,
  bucketDateKey,
  buildDiscordThreadUrl,
  buildGrafanaExploreUrl,
  buildMentionKey,
  buildOpsConfigDTO,
  buildRepoEntryTypeMap,
  buildSlackThreadUrl,
  canGrantLevel,
  canModifyMember,
  canonicalizeTagMentions,
  chatProviderExclusiveReason,
  checkpointDigestRefSchema,
  checkpointKeySchema,
  clampDescription,
  clampDescriptionField,
  clearOpsEnvFields,
  coerceProjectSettings,
  computeBootProgress,
  computeMemoryBounds,
  computePodRatio,
  computeTotalXp,
  contextLinkCheckKey,
  conveyorCapabilitySchema,
  createAgentSessionRoom,
  createProjectRoom,
  deriveConnectionState,
  deriveMessageSource,
  effectiveMessageKind,
  encodePtyStreamFrame,
  exceptionalLogKind,
  exceptionalLogSignature,
  exceptionalLogSummary,
  extractRepoPaths,
  extractTestTitles,
  findContextPathWarning,
  findContextPathWarnings,
  formatCompactNumber,
  formatCompactSigned,
  formatCompactUsd,
  formatHeartbeatAge,
  formatModelId,
  generateBucketRange,
  generateDateRange,
  generateHourRange,
  generateWeekRange,
  getCatalogModelLabel,
  getStreamMentionTarget,
  getStreamMentionTargetFromKeys,
  getToolsByCategory,
  groupExceptionalLogEntries,
  hasEffectiveOverview,
  hasForcePrefix,
  hasMentionToken,
  hasTagMentionToken,
  hasTaskPlan,
  isActiveWorkStatus,
  isAllowablePreviewPort,
  isAwaitingUserInput,
  isBootStepKey,
  isComputeDisabled,
  isCreatedDescStatus,
  isCuid,
  isEmbeddableFile,
  isExceptionalLogEntry,
  isHigherRisk,
  isHourlyRange,
  isIdentifiedWorkStatus,
  isModelProvider,
  isOpsConfigured,
  isOpsEnvConfigured,
  isOpsEnvConfiguredDTO,
  isPlaceholderLocator,
  isPlainProjectSettings,
  isPodReportableBootStep,
  isPodSizeName,
  isPrProducingMode,
  isPtyStreamPort,
  isSupportedWireFormat,
  isValidAutopilotConfig,
  judgeContextLink,
  levelFromXp,
  linkNeedsContent,
  locatorMatchesContent,
  matchTagsByPaths,
  meetsContributorLevel,
  mergeOpsEnvSettings,
  messageMatchesActivityFilters,
  normalizeContextPath,
  normalizeReportStatus,
  normalizeTaskStatus,
  overviewSourceLink,
  parseContextPathChecks,
  parseMentions,
  parseModelId,
  parsePtyStreamFrame,
  parseTagContextLinks,
  parseTagContextPaths,
  pickPreferredPreviewPort,
  prestigeCycleCost,
  prestigeFromXp,
  projectCheckpointSettingsSchema,
  replaceMentionTokens,
  requiredContributorLevel,
  resolveAgentTrigger,
  resolveAllowedPreviewPorts,
  resolveAvailableOpsTabs,
  resolveAvailableOpsTabsForEnv,
  resolveChatProvider,
  resolveCodeLocation,
  resolveCodeReviewMode,
  resolveComputeProvider,
  resolveForgeSettings,
  resolveForgejoConnectionSettings,
  resolveGcpEnvResources,
  resolveGrafanaSettings,
  resolveMaxOpenPrs,
  resolveMentions,
  resolveOpsEnv,
  resolveOpsSettings,
  resolveProjectCheckpointSettings,
  resolveProjectFunctionConfig,
  resolveStatDateRange,
  resolveStatGranularity,
  resolveTagOverview,
  resolveTriageSettings,
  resolveVerifiedLinksPrCheck,
  riskFromReviewSeverities,
  riskLevelForValue,
  riskLevelSchema,
  riskRank,
  sanitizeBootTimeline,
  sanitizeDiscoveredPorts,
  sanitizeSessionPreviewPorts,
  serializeMention,
  serviceRoom,
  setOpsReadOnly,
  sidecarRequestTotals,
  stripForcePrefix,
  stripMentionTokens,
  stripStreamMentionText,
  summarizeBootEstimates,
  summarizeReportBody,
  tagSnapshotToText,
  tagSnapshotsEqual,
  taskStatusColor,
  taskStatusColorInt,
  toCardFromTaskDTO,
  toContributorLevel,
  todayInProjectTZ,
  userRoom,
  validateElasticsearchUrl,
  validateRedisUrl,
  validateStatusForType,
  verificationForLink,
  weekStartKey,
  withLinkVerification,
  xpToReachLevel,
  zenWireFormat
};
